import React from 'react';
import { HomePage } from '@/pages';
import { BareLayout, withPlanGate } from '@/components/layouts';
import { ALL_ROLES, ACCESS, USER_ROLES } from './userRoles';
import { FEATURE_CODES } from './featuresCode';
import { EventFeatureNavCtx, featureKnownToHaveData } from './eventFeatureNav';

// Helper for lazy loading named exports
/**
 * A lazily-loaded page, carrying its own `preload()`.
 *
 * ⚠⚠ THE `preload` IS WHY A NAVIGATION STOPS FLASHING. Every page here is a separate chunk, so the
 * FIRST visit to any page has to download before it can render — which is exactly the "the whole
 * layout reloads, but the second visit is fine" report. `React.lazy` gives no way to start that
 * download early, so the import function is kept on the component and `preloadRoute()` calls it the
 * moment a host so much as points at the entry. By the time the click lands the chunk is usually
 * already in memory and `React.lazy` resolves without suspending at all.
 * ⚠ Calling it repeatedly is free — the browser caches the module, and a second `import()` of the
 * same specifier returns the same promise.
 */
type PreloadableComponent = React.ComponentType<any> & { preload?: () => Promise<unknown> };

const lazyNamed = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ [key: string]: T }>,
  componentName: string,
) => {
  const Lazy = React.lazy(() =>
    importFn().then((module) => ({ default: module[componentName] as T })),
  ) as unknown as PreloadableComponent;

  Lazy.preload = importFn;

  return Lazy as unknown as T;
};

// One generic placeholder component backs every not-yet-built page.
const PlaceholderPage = lazyNamed(() => import('@/pages/PlaceholderPage/PlaceholderPage'), 'PlaceholderPage');
// A2b #57 — notifications. ⚠ The route, the page and the bell arrive TOGETHER (see the block below).
const NotificationsPage = lazyNamed(() => import('@/pages/private/NotificationsPage/NotificationsPage'), 'NotificationsPage');

// ── Public pages
const NotFoundPage = lazyNamed(() => import('@/pages/public/NotFoundPage/NotFoundPage'), 'NotFoundPage');
const ServerErrorPage = lazyNamed(() => import('@/pages/public/ServerErrorPage/ServerErrorPage'), 'ServerErrorPage');
const SigninPage = lazyNamed(() => import('@/pages/public/SigninPage/SigninPage'), 'SigninPage');
const CrewAccessPage = lazyNamed(() => import('@/pages/public/CrewAccessPage/CrewAccessPage'), 'CrewAccessPage');
const SignupPage = lazyNamed(() => import('@/pages/public/SignupPage/SignupPage'), 'SignupPage');
const ForgotPasswordPage = lazyNamed(
  () => import('@/pages/public/ForgotPasswordPage/ForgotPasswordPage'),
  'ForgotPasswordPage',
);
const ResetPasswordPage = lazyNamed(
  () => import('@/pages/public/ResetPasswordPage/ResetPasswordPage'),
  'ResetPasswordPage',
);
const AcceptInvitationPage = lazyNamed(
  () => import('@/pages/public/AcceptInvitationPage/AcceptInvitationPage'),
  'AcceptInvitationPage',
);

// ── Existing (real) private pages
const TagsPage = lazyNamed(() => import('@/pages/private/TagsPage/TagsPage'), 'TagsPage');
const ContactsPage = lazyNamed(() => import('@/pages/private/ContactsPage/ContactsPage'), 'ContactsPage');
const HighlightsPage = lazyNamed(() => import('@/pages/private/HighlightsPage/HighlightsPage'), 'HighlightsPage');
const CategoriesPage = lazyNamed(() => import('@/pages/private/CategoriesPage/CategoriesPage'), 'CategoriesPage');
const PoliciesPage = lazyNamed(() => import('@/pages/private/PoliciesPage/PoliciesPage'), 'PoliciesPage');
const ThemesPage = lazyNamed(() => import('@/pages/private/ThemesPage/ThemesPage'), 'ThemesPage');
const ThemeEditorPage = lazyNamed(() => import('@/pages/private/ThemeEditorPage/ThemeEditorPage'), 'ThemeEditorPage');
const VouchersPage = lazyNamed(() => import('@/pages/private/VouchersPage/VouchersPage'), 'VouchersPage');
const TaxSettingsPage = lazyNamed(
  () => import('@/pages/private/TaxSettingsPage/TaxSettingsPage'),
  'TaxSettingsPage',
);
const RentalAssetsPage = lazyNamed(
  () => import('@/pages/private/RentalAssetsPage/RentalAssetsPage'),
  'RentalAssetsPage',
);
const InventoryItemsPage = lazyNamed(
  () => import('@/pages/private/InventoryItemsPage/InventoryItemsPage'),
  'InventoryItemsPage',
);
const ArtistsPage = lazyNamed(() => import('@/pages/private/ArtistsPage/ArtistsPage'), 'ArtistsPage');
const OrganizerProfilesPage = lazyNamed(
  () => import('@/pages/private/OrganizerProfilesPage/OrganizerProfilesPage'),
  'OrganizerProfilesPage',
);
const OrganizerProfilePage = lazyNamed(
  () => import('@/pages/private/OrganizerProfilePage/OrganizerProfilePage'),
  'OrganizerProfilePage',
);
const OrganizerProfileThemePage = lazyNamed(
  () => import('@/pages/private/OrganizerProfileThemePage/OrganizerProfileThemePage'),
  'OrganizerProfileThemePage',
);
const InventoryStockPage = lazyNamed(
  () => import('@/pages/private/InventoryOpsPage/InventoryOpsPage'),
  'InventoryStockPage',
);
const InventoryOperationsPage = lazyNamed(
  () => import('@/pages/private/InventoryOpsPage/InventoryOpsPage'),
  'InventoryOperationsPage',
);
const InventoryHistoryPage = lazyNamed(
  () => import('@/pages/private/InventoryOpsPage/InventoryOpsPage'),
  'InventoryHistoryPage',
);
const InventoryRedirect = lazyNamed(
  () => import('@/pages/private/InventoryOpsPage/InventoryOpsPage'),
  'InventoryRedirect',
);
const PurchaseOrdersPage = lazyNamed(
  () => import('@/pages/private/PurchaseOrdersPage/PurchaseOrdersPage'),
  'PurchaseOrdersPage',
);
const PurchaseOrderPrintPage = lazyNamed(
  () => import('@/pages/private/PurchaseOrderPrintPage/PurchaseOrderPrintPage'),
  'PurchaseOrderPrintPage',
);
const PartnerReceiptPrintPage = lazyNamed(
  () => import('@/pages/private/PartnerReceiptPrintPage/PartnerReceiptPrintPage'),
  'PartnerReceiptPrintPage',
);
const PartnerPaymentReceiptPrintPage = lazyNamed(
  () => import('@/pages/private/PartnerPaymentReceiptPrintPage/PartnerPaymentReceiptPrintPage'),
  'PartnerPaymentReceiptPrintPage',
);
const ReceiptPrintPage = lazyNamed(
  () => import('@/pages/private/ReceiptPrintPage/ReceiptPrintPage'),
  'ReceiptPrintPage',
);
const SpendingReceiptPrintPage = lazyNamed(
  () => import('@/pages/private/SpendingReceiptPrintPage/SpendingReceiptPrintPage'),
  'SpendingReceiptPrintPage',
);
const InventoryLabelsPrintPage = lazyNamed(
  () => import('@/pages/private/InventoryLabelsPrintPage/InventoryLabelsPrintPage'),
  'InventoryLabelsPrintPage',
);
const VolunteerRolesPage = lazyNamed(
  () => import('@/pages/private/VolunteerRolesPage/VolunteerRolesPage'),
  'VolunteerRolesPage',
);
const PartnerPackagesPage = lazyNamed(
  () => import('@/pages/private/PartnerPackagesPage/PartnerPackagesPage'),
  'PartnerPackagesPage',
);
const StoragePage = lazyNamed(() => import('@/pages/private/StoragePage/StoragePage'), 'StoragePage');
const EventsPage = lazyNamed(() => import('@/pages/private/EventsPage/EventsPage'), 'EventsPage');
const CrewEventsPage = lazyNamed(() => import('@/pages/private/CrewEventsPage/CrewEventsPage'), 'CrewEventsPage');
const CreateEventPage = lazyNamed(() => import('@/pages/private/CreateEventPage/CreateEventPage'), 'CreateEventPage');
const EventDashboardPage = lazyNamed(
  () => import('@/pages/private/EventDashboardPage/EventDashboardPage'),
  'EventDashboardPage',
);
const EventFeaturesPage = lazyNamed(
  () => import('@/pages/private/EventFeaturesPage/EventFeaturesPage'),
  'EventFeaturesPage',
);
const EventDetailsPage = lazyNamed(
  () => import('@/pages/private/EventDetailsPage/EventDetailsPage'),
  'EventDetailsPage',
);
const EventWebsitePage = lazyNamed(
  () => import('@/pages/private/EventWebsitePage/EventWebsitePage'),
  'EventWebsitePage',
);
const EventPageEditorPage = lazyNamed(
  () => import('@/pages/private/EventPageEditorPage/EventPageEditorPage'),
  'EventPageEditorPage',
);
const EventThemePage = lazyNamed(() => import('@/pages/private/EventThemePage/EventThemePage'), 'EventThemePage');
const EventQuestionsPage = lazyNamed(
  () => import('@/pages/private/EventQuestionsPage/EventQuestionsPage'),
  'EventQuestionsPage',
);
const EventCompliancePage = lazyNamed(
  () => import('@/pages/private/EventCompliancePage/EventCompliancePage'),
  'EventCompliancePage',
);
const EventInvitationsPage = lazyNamed(
  () => import('@/pages/private/EventInvitationsPage/EventInvitationsPage'),
  'EventInvitationsPage',
);
const EventMessagesPage = lazyNamed(
  () => import('@/pages/private/EventMessagesPage/EventMessagesPage'),
  'EventMessagesPage',
);
const EventUpdatesPage = lazyNamed(
  () => import('@/pages/private/EventUpdatesPage/EventUpdatesPage'),
  'EventUpdatesPage',
);
const SentMessagesPage = lazyNamed(
  () => import('@/pages/private/SentMessagesPage/SentMessagesPage'),
  'SentMessagesPage',
);
const IncomingMessagesPage = lazyNamed(
  () => import('@/pages/private/IncomingMessagesPage/IncomingMessagesPage'),
  'IncomingMessagesPage',
);
const RemindersPage = lazyNamed(() => import('@/pages/private/RemindersPage/RemindersPage'), 'RemindersPage');
const EventCommentsPage = lazyNamed(
  () => import('@/pages/private/EventCommentsPage/EventCommentsPage'),
  'EventCommentsPage',
);
const PolicyPrintPage = lazyNamed(() => import('@/pages/private/PolicyPrintPage/PolicyPrintPage'), 'PolicyPrintPage');
const EventDiscountCodesPage = lazyNamed(
  () => import('@/pages/private/EventDiscountCodesPage/EventDiscountCodesPage'),
  'EventDiscountCodesPage',
);
const EventCouponsPage = lazyNamed(
  () => import('@/pages/private/EventCouponsPage/EventCouponsPage'),
  'EventCouponsPage',
);
const EventCouponRedeemPage = lazyNamed(
  () => import('@/pages/private/EventCouponRedeemPage/EventCouponRedeemPage'),
  'EventCouponRedeemPage',
);
const EventTicketsPage = lazyNamed(
  () => import('@/pages/private/EventTicketsPage/EventTicketsPage'),
  'EventTicketsPage',
);
const EventGuestsPage = lazyNamed(() => import('@/pages/private/EventGuestsPage/EventGuestsPage'), 'EventGuestsPage');
const EventPoisPage = lazyNamed(() => import('@/pages/private/EventPoisPage/EventPoisPage'), 'EventPoisPage');
const PoiPrintPage = lazyNamed(() => import('@/pages/private/PoiPrintPage/PoiPrintPage'), 'PoiPrintPage');
const EventAccommodationsPage = lazyNamed(
  () => import('@/pages/private/EventAccommodationsPage/EventAccommodationsPage'),
  'EventAccommodationsPage',
);
const EventPartnersPage = lazyNamed(
  () => import('@/pages/private/EventPartnersPage/EventPartnersPage'),
  'EventPartnersPage',
);
const EventPartnerPackagesPage = lazyNamed(
  () => import('@/pages/private/EventPartnerPackagesPage/EventPartnerPackagesPage'),
  'EventPartnerPackagesPage',
);
const MonthlyDonorsPage = lazyNamed(() => import('@/pages/private/MonthlyDonorsPage/MonthlyDonorsPage'), 'MonthlyDonorsPage');
const EventDonationsPage = lazyNamed(
  () => import('@/pages/private/EventDonationsPage/EventDonationsPage'),
  'EventDonationsPage',
);
const EventPartnerApplicationsPage = lazyNamed(
  () => import('@/pages/private/EventPartnerApplicationsPage/EventPartnerApplicationsPage'),
  'EventPartnerApplicationsPage',
);
const EventArtistsPage = lazyNamed(
  () => import('@/pages/private/EventArtistsPage/EventArtistsPage'),
  'EventArtistsPage',
);
const EventTicketSalesPage = lazyNamed(
  () => import('@/pages/private/EventTicketSalesPage/EventTicketSalesPage'),
  'EventTicketSalesPage',
);
const EventTransactionsPage = lazyNamed(
  () => import('@/pages/private/EventTransactionsPage/EventTransactionsPage'),
  'EventTransactionsPage',
);
const EventVolunteerApplicationsPage = lazyNamed(
  () => import('@/pages/private/EventVolunteerApplicationsPage/EventVolunteerApplicationsPage'),
  'EventVolunteerApplicationsPage',
);
const EventVolunteersPage = lazyNamed(
  () => import('@/pages/private/EventVolunteersPage/EventVolunteersPage'),
  'EventVolunteersPage',
);
const EventVolunteerCheckinPage = lazyNamed(
  () => import('@/pages/private/EventVolunteerCheckinPage/EventVolunteerCheckinPage'),
  'EventVolunteerCheckinPage',
);
const EventCheckinPage = lazyNamed(
  () => import('@/pages/private/EventCheckinPage/EventCheckinPage'),
  'EventCheckinPage',
);
const VolunteerSignupPrintPage = lazyNamed(
  () => import('@/pages/private/VolunteerSignupPrintPage/VolunteerSignupPrintPage'),
  'VolunteerSignupPrintPage',
);
const EventRunSheetPrintPage = lazyNamed(
  () => import('@/pages/private/EventRunSheetPrintPage/EventRunSheetPrintPage'),
  'EventRunSheetPrintPage',
);

const EventCallSheetPrintPage = lazyNamed(
  () => import('@/pages/private/EventCallSheetPrintPage/EventCallSheetPrintPage'),
  'EventCallSheetPrintPage',
);

const GuestListPrintPage = lazyNamed(
  () => import('@/pages/private/GuestListPrintPage/GuestListPrintPage'),
  'GuestListPrintPage',
);

const EventCouponsPrintPage = lazyNamed(
  () => import('@/pages/private/EventCouponsPrintPage/EventCouponsPrintPage'),
  'EventCouponsPrintPage',
);
const EventQrTagsPrintPage = lazyNamed(
  () => import('@/pages/private/EventQrTagsPrintPage/EventQrTagsPrintPage'),
  'EventQrTagsPrintPage',
);
const EventTicketsPrintPage = lazyNamed(
  () => import('@/pages/private/EventTicketsPrintPage/EventTicketsPrintPage'),
  'EventTicketsPrintPage',
);

const EventBadgesPrintPage = lazyNamed(
  () => import('@/pages/private/EventBadgesPrintPage/EventBadgesPrintPage'),
  'EventBadgesPrintPage',
);

const VolunteerRosterPrintPage = lazyNamed(
  () => import('@/pages/private/VolunteerRosterPrintPage/VolunteerRosterPrintPage'),
  'VolunteerRosterPrintPage',
);
const EventStaffPayPage = lazyNamed(() => import('@/pages/private/EventStaffPayPage/EventStaffPayPage'), 'EventStaffPayPage');
const StaffPayTimesheetPrintPage = lazyNamed(() => import('@/pages/private/StaffPayTimesheetPrintPage/StaffPayTimesheetPrintPage'), 'StaffPayTimesheetPrintPage');
const AccommodationFormPrintPage = lazyNamed(
  () => import('@/pages/private/AccommodationFormPrintPage/AccommodationFormPrintPage'),
  'AccommodationFormPrintPage',
);
const EventSignupItemsPage = lazyNamed(
  () => import('@/pages/private/EventSignupItemsPage/EventSignupItemsPage'),
  'EventSignupItemsPage',
);
const SignupFormPrintPage = lazyNamed(
  () => import('@/pages/private/SignupFormPrintPage/SignupFormPrintPage'),
  'SignupFormPrintPage',
);
const EventPollQuestionsPage = lazyNamed(
  () => import('@/pages/private/EventPollQuestionsPage/EventPollQuestionsPage'),
  'EventPollQuestionsPage',
);
const PollFormPrintPage = lazyNamed(
  () => import('@/pages/private/PollFormPrintPage/PollFormPrintPage'),
  'PollFormPrintPage',
);
const EventNotesPage = lazyNamed(() => import('@/pages/private/EventNotesPage/EventNotesPage'), 'EventNotesPage');
const EventMapPage = lazyNamed(() => import('@/pages/private/EventMapPage/EventMapPage'), 'EventMapPage');
const EventTasksPage = lazyNamed(() => import('@/pages/private/EventTasksPage/EventTasksPage'), 'EventTasksPage');
const EventBudgetPage = lazyNamed(() => import('@/pages/private/EventBudgetPage/EventBudgetPage'), 'EventBudgetPage');
const EventFinancePage = lazyNamed(() => import('@/pages/private/EventFinancePage/EventFinancePage'), 'EventFinancePage');
const EventQuestionReportPage = lazyNamed(
  () => import('@/pages/private/EventQuestionReportPage/EventQuestionReportPage'),
  'EventQuestionReportPage',
);
const EventQuestionReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventQuestionReportPrintPage/EventQuestionReportPrintPage'),
  'EventQuestionReportPrintPage',
);
// ROADMAP #236 — the guests' answers sheet (one row per guest, the questions the host picked).
const EventAnswerSheetPrintPage = lazyNamed(
  () => import('@/pages/private/EventAnswerSheetPrintPage/EventAnswerSheetPrintPage'),
  'EventAnswerSheetPrintPage',
);
const EventSignupReportPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPage/EventSignupReportPage'),
  'EventSignupReportPage',
);
const EventSignupReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPrintPage/EventSignupReportPrintPage'),
  'EventSignupReportPrintPage',
);
// #46 / #47 — the SAME page and sheet components as the signup-list report above, given a
// different kind descriptor. One server aggregate, one page, three routes.
const EventPoiReportPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPage/EventSignupReportPage'),
  'EventPoiReportPage',
);
const EventPoiReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPrintPage/EventSignupReportPrintPage'),
  'EventPoiReportPrintPage',
);
const EventAccommodationReportPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPage/EventSignupReportPage'),
  'EventAccommodationReportPage',
);
const EventAccommodationReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventSignupReportPrintPage/EventSignupReportPrintPage'),
  'EventAccommodationReportPrintPage',
);
const EventAnalyticsPage = lazyNamed(
  () => import('@/pages/private/EventAnalyticsPage/EventAnalyticsPage'),
  'EventAnalyticsPage',
);
// #45 — polls have their OWN tables and their own server read, so this is its own page rather than
// another kind of the signup family's shared one.
const EventPollReportPage = lazyNamed(
  () => import('@/pages/private/EventPollReportPage/EventPollReportPage'),
  'EventPollReportPage',
);
const EventPollReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventPollReportPrintPage/EventPollReportPrintPage'),
  'EventPollReportPrintPage',
);
// #48 — travel reads the itinerary tables, so it is its own page too.
const EventTravelReportPage = lazyNamed(
  () => import('@/pages/private/EventTravelReportPage/EventTravelReportPage'),
  'EventTravelReportPage',
);
const EventTravelReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventTravelReportPrintPage/EventTravelReportPrintPage'),
  'EventTravelReportPrintPage',
);
// #179 — attendance: who came, when, through where (on the insights plan feature).
const EventArrivalReportPage = lazyNamed(
  () => import('@/pages/private/EventArrivalReportPage/EventArrivalReportPage'),
  'EventArrivalReportPage',
);
const EventArrivalReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventArrivalReportPrintPage/EventArrivalReportPrintPage'),
  'EventArrivalReportPrintPage',
);
const EventTicketSalesReportPage = lazyNamed(
  () => import('@/pages/private/EventTicketSalesReportPage/EventTicketSalesReportPage'),
  'EventTicketSalesReportPage',
);
const EventTicketSalesReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventTicketSalesReportPrintPage/EventTicketSalesReportPrintPage'),
  'EventTicketSalesReportPrintPage',
);
const EventAddonReportPage = lazyNamed(() => import('@/pages/private/EventAddonReportPage/EventAddonReportPage'), 'EventAddonReportPage');
const EventAddonReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventAddonReportPrintPage/EventAddonReportPrintPage'),
  'EventAddonReportPrintPage',
);
const EventVoucherReportPage = lazyNamed(() => import('@/pages/private/EventVoucherReportPage/EventVoucherReportPage'), 'EventVoucherReportPage');
const EventVoucherReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventVoucherReportPrintPage/EventVoucherReportPrintPage'),
  'EventVoucherReportPrintPage',
);
const EventVolunteerReportPage = lazyNamed(() => import('@/pages/private/EventVolunteerReportPage/EventVolunteerReportPage'), 'EventVolunteerReportPage');
const EventVolunteerReportPrintPage = lazyNamed(
  () => import('@/pages/private/EventVolunteerReportPrintPage/EventVolunteerReportPrintPage'),
  'EventVolunteerReportPrintPage',
);
const EventFinancePrintPage = lazyNamed(
  () => import('@/pages/private/EventFinancePrintPage/EventFinancePrintPage'),
  'EventFinancePrintPage',
);
const EventForecastPrintPage = lazyNamed(
  () => import('@/pages/private/EventForecastPrintPage/EventForecastPrintPage'),
  'EventForecastPrintPage',
);
const EventRentalsPage = lazyNamed(
  () => import('@/pages/private/EventRentalsPage/EventRentalsPage'),
  'EventRentalsPage',
);
const EventLostFoundPage = lazyNamed(
  () => import('@/pages/private/EventLostFoundPage/EventLostFoundPage'),
  'EventLostFoundPage',
);
const EventQrTagsPage = lazyNamed(
  () => import('@/pages/private/EventQrTagsPage/EventQrTagsPage'),
  'EventQrTagsPage',
);
const EventVendorsPage = lazyNamed(
  () => import('@/pages/private/EventVendorsPage/EventVendorsPage'),
  'EventVendorsPage',
);
const EventVendorHistoryPage = lazyNamed(
  () => import('@/pages/private/EventVendorHistoryPage/EventVendorHistoryPage'),
  'EventVendorHistoryPage',
);
const EventVendorPage = lazyNamed(
  () => import('@/pages/private/EventVendorPage/EventVendorPage'),
  'EventVendorPage',
);
const EventInventoryStockPage = lazyNamed(
  () => import('@/pages/private/EventInventoryPage/EventInventoryPage'),
  'EventInventoryStockPage',
);
const EventInventoryOperationsPage = lazyNamed(
  () => import('@/pages/private/EventInventoryPage/EventInventoryPage'),
  'EventInventoryOperationsPage',
);
const EventInventoryHistoryPage = lazyNamed(
  () => import('@/pages/private/EventInventoryPage/EventInventoryPage'),
  'EventInventoryHistoryPage',
);
const EventInventoryRedirect = lazyNamed(
  () => import('@/pages/private/EventInventoryPage/EventInventoryPage'),
  'EventInventoryRedirect',
);
const EventTimelinePage = lazyNamed(
  () => import('@/pages/private/EventTimelinePage/EventTimelinePage'),
  'EventTimelinePage',
);
const EventTravelPage = lazyNamed(() => import('@/pages/private/EventTravelPage/EventTravelPage'), 'EventTravelPage');
const EventTalliesPage = lazyNamed(
  () => import('@/pages/private/EventTalliesPage/EventTalliesPage'),
  'EventTalliesPage',
);
const EventRaceTrackingPage = lazyNamed(
  () => import('@/pages/private/EventRaceTrackingPage/EventRaceTrackingPage'),
  'EventRaceTrackingPage',
);
const EventRaceResultsPage = lazyNamed(
  () => import('@/pages/private/EventRaceResultsPage/EventRaceResultsPage'),
  'EventRaceResultsPage',
);
const EventFilesPage = lazyNamed(() => import('@/pages/private/EventFilesPage/EventFilesPage'), 'EventFilesPage');
const MarketingReportPage = lazyNamed(
  () => import('@/pages/private/MarketingReportPage/MarketingReportPage'),
  'MarketingReportPage',
);
const EventMarketingReportPage = lazyNamed(
  () => import('@/pages/private/EventMarketingReportPage/EventMarketingReportPage'),
  'EventMarketingReportPage',
);
const MarketingChannelsPage = lazyNamed(
  () => import('@/pages/private/MarketingChannelsPage/MarketingChannelsPage'),
  'MarketingChannelsPage',
);
const EventMarketingChannelsPage = lazyNamed(
  () => import('@/pages/private/EventMarketingChannelsPage/EventMarketingChannelsPage'),
  'EventMarketingChannelsPage',
);
const EventCrewPage = lazyNamed(() => import('@/pages/private/EventGuestsPage/EventCrewPage'), 'EventCrewPage');
const EventArrivalsPage = lazyNamed(() => import('@/pages/private/EventGuestsPage/EventArrivalsPage'), 'EventArrivalsPage');
const EventGuestGroupsPage = lazyNamed(
  () => import('@/pages/private/EventGuestGroupsPage/EventGuestGroupsPage'),
  'EventGuestGroupsPage',
);
const EventDrawsPage = lazyNamed(() => import('@/pages/private/EventDrawsPage/EventDrawsPage'), 'EventDrawsPage');
const DrawWinnersPrintPage = lazyNamed(
  () => import('@/pages/private/DrawWinnersPrintPage/DrawWinnersPrintPage'),
  'DrawWinnersPrintPage',
);
const EventGamesPage = lazyNamed(() => import('@/pages/private/EventGamesPage/EventGamesPage'), 'EventGamesPage');
const EventSchedulePage = lazyNamed(
  () => import('@/pages/private/EventSchedulePage/EventSchedulePage'),
  'EventSchedulePage',
);
const VenuesPage = lazyNamed(() => import('@/pages/private/VenuesPage/VenuesPage'), 'VenuesPage');
const VendorsPage = lazyNamed(() => import('@/pages/private/VenuesPage/VenuesPage'), 'VendorsPage');
const LibraryVendorPage = lazyNamed(() => import('@/pages/private/LibraryVendorPage/LibraryVendorPage'), 'LibraryVendorPage');
const MembershipsPage = lazyNamed(() => import('@/pages/private/MembershipsPage/MembershipsPage'), 'MembershipsPage');
const MembershipTypesPage = lazyNamed(
  () => import('@/pages/private/MembershipTypesPage/MembershipTypesPage'),
  'MembershipTypesPage',
);
// ROADMAP #204 — donations: the gifts, the causes, the receipt details (one module, three tabs).
const DonationsPage = lazyNamed(() => import('@/pages/private/DonationsPage/DonationsPage'), 'DonationsPage');
const CausesPage = lazyNamed(() => import('@/pages/private/CausesPage/CausesPage'), 'CausesPage');
const ReceiptDetailsPage = lazyNamed(() => import('@/pages/private/ReceiptDetailsPage/ReceiptDetailsPage'), 'ReceiptDetailsPage');
const AccountSettingsPage = lazyNamed(
  () => import('@/pages/private/AccountSettingsPage/AccountSettingsPage'),
  'AccountSettingsPage',
);
const SubscriptionPage = lazyNamed(
  () => import('@/pages/private/SubscriptionPage/SubscriptionPage'),
  'SubscriptionPage',
);
const StripeConnectPage = lazyNamed(
  () => import('@/pages/private/StripeConnectPage/StripeConnectPage'),
  'StripeConnectPage',
);
const PaymentsPage = lazyNamed(() => import('@/pages/private/PaymentsPage/PaymentsPage'), 'PaymentsPage');
const UserInvitationsPage = lazyNamed(
  () => import('@/pages/private/UserInvitationsPage/UserInvitationsPage'),
  'UserInvitationsPage',
);
const UsersPage = lazyNamed(() => import('@/pages/private/UsersPage/UsersPage'), 'UsersPage');
const EventDefaultsPage = lazyNamed(
  () => import('@/pages/private/EventDefaultsPage/EventDefaultsPage'),
  'EventDefaultsPage',
);
const UserProfilePage = lazyNamed(() => import('@/pages/private/UserProfilePage/UserProfilePage'), 'UserProfilePage');
const OnboardingPage = lazyNamed(() => import('@/pages/private/OnboardingPage/OnboardingPage'), 'OnboardingPage');

/**
 * Context handed to a nav item's `visibleWhen` predicate at render time. This is
 * the ONE place all conditional-nav logic reads from — extend it as new signals
 * come online (ticketing today; account/crew role, plan features, "feature already
 * used" auto-reveal, and per-user hidden pages next). Kept deliberately small so a
 * predicate stays cheap.
 */
export interface NavVisibilityCtx {
  /** The event whose workspace is open (null in the account workspace). */
  event?: {
    eventMode?: string | null;
    guestTracking?: string | null;
  } | null;
  /**
   * Crew mode (docs/CREW-ACCESS.md): true when the caller is working this event as
   * CREW (not its owner). In crew mode the event nav is filtered by the caller's
   * CREW ROLE (see `crewRoles`) against each item's `roles` — exactly like account
   * roles, but the viewer's role is their event guest_role. So a co_host sees every
   * page whose `roles` include 'co_host' (everything except delete), staff sees only
   * the guest-facing pages, etc. Every event page is hidden from the menu if its
   * `roles` don't include the crew role (the route stays registered; the BE denies
   * access anyway). Owners never enter crew mode, so their nav is unaffected.
   */
  crewMode?: boolean;
  /** The crew role(s) the caller has on this event (their guest_role) — matched against item.roles. */
  crewRoles?: string[];
  /**
   * ROADMAP A2b #63 — the event's features (`settings.features`) and which of them have data. Read by
   * `markEventFeatureNav` (what moves behind "Show more") and by `ticketedOnly` (a selling page with sales on it is never
   * hidden). Null/absent = not known yet.
   */
  features?: EventFeatureNavCtx | null;
  // Reserved for the next iterations — add fields here (not new mechanisms):
  //   accountRoles?: string[];              // account-role gating
  //   hiddenKeys?: Set<string>;             // per-user "hide pages I don't use"
}

export interface SiteMapItem {
  name: string;
  subtitle?: string;
  bottomNavBar?: number | null | undefined;
  /**
   * Overrides for how a bottom-tab item appears in the mobile tab bar, used when
   * it should differ from the sidebar entry. The tab navigates to `url` (or, for
   * a nav-group with no own `url`, its first visible child's `url`).
   */
  bottomNavBarLabel?: string;
  bottomNavBarIcon?: string;
  url?: string;
  urlMask?: string;
  /**
   * Stable identifier for a route, independent of its URL. Components that need
   * to navigate to an entity look the path up by this key (see getRoutePath),
   * so the URL string lives ONLY here in the sitemap — change it in one place.
   */
  key?: string;
  component?: React.ComponentType<any>;
  layout?: React.ComponentType<any>;
  visible?: boolean;
  roles?: string[];
  items?: SiteMapItem[];
  options?: {
    translationKey?: string;
  };
  icon?: string;
  iconColor?: string;
  /** Which workspace this item belongs to. Defaults to "account". */
  scope?: 'account' | 'event';
  /**
   * Optional DYNAMIC visibility for the MENU only, evaluated at nav-render time
   * against live context (see NavVisibilityCtx). Return false to hide from the
   * sidebar / tab bar / "More" — the ROUTE stays registered (routes come from the
   * raw siteMap), so a deep link still resolves and can later show a premium/upsell
   * gate instead of a 404. Omit for always-visible (still subject to the static
   * `visible` + `roles` gates). This is the seam for role / feature / user-hide
   * gating to plug into later.
   */
  visibleWhen?: (ctx: NavVisibilityCtx) => boolean;
  /**
   * ROADMAP A2b #63 — the EVENT feature this page belongs to (a code from `config/eventSetup.generated`
   * EVENT_FEATURE_CODES). An event that does not use the feature shows the page behind its section's "Show more"
   * instead of in the menu — unless the page has data, which always keeps it (see `config/eventFeatureNav.ts`).
   * Omit for pages every event has (the guest list, communications, the website's pages).
   * ⚠ Not the same thing as `featureCode`, which is the PLAN feature that unlocks the page.
   */
  eventFeature?: string;
  /** DERIVED by `markEventFeatureNav`, never declared: this item sits behind "Show more". */
  navMore?: boolean;
  /** DERIVED by `markEventFeatureNav`, never declared: the account's plan does not include this page. */
  navPremium?: boolean;
  /**
   * ⭐ THE PLAN FEATURE THIS PAGE BELONGS TO — the one place page-level gating is declared.
   *
   * When set, the page is wrapped so a host whose plan lacks the feature gets the upgrade screen
   * (with the sidebar and header intact) INSTEAD of the page, and the page is never mounted. The
   * alternative — render it and put a notice on top — means somebody fills in a budget item to be
   * refused at the end, which is the thing this exists to stop.
   *
   * ⚠ ONLY FOR FEATURES DECLARED `gate: 'page'` (eventsmagic-api `boundary/appFeatures.ts` →
   * `FEATURE_GATE_KINDS`). A NUMERIC ceiling must never be bound here: a host at 5 of 5 questions
   * HAS five questions, and locking the page would hide them. Those stay add-only, gated inside
   * the page with `PlanFeatureNotice`. `npm run check-entitlement` enforces the split.
   *
   * ⚠ Bind EVERY page of the feature's module, including read-only sub-views — a locked module
   * with one reachable page is worse than either answer.
   */
  featureCode?: string;
  /**
   * Which block of `i18n plans.json → gates.<gateKey>` this page's lock screen shows under
   * "What you'd get".
   *
   * ⚠ PER PAGE, not per feature, and that is the whole point. One `featureCode` owns a module —
   * `production` covers Budget, Rentals, Inventory, Travel, Map, Volunteers, Race timing and
   * Purchase orders — so the feature's own one-liner is the same sentence on eight screens and
   * describes none of them. A host who clicked Budget wants to read about Budget.
   *
   * ⚠ Inherited by child routes alongside `featureCode`, so `…/budget/add` shows the same copy.
   * Omit it and the lock falls back to the generic feature description: weaker, never broken.
   */
  gateKey?: string;
  /**
   * ROADMAP #238 — this page is an OUTPUT of its feature (a print of a report): a draft or a test run whose plan lacks the
   * feature does NOT open it (reports are viewed there, never taken away). Declared on the print route itself; not inherited.
   */
  output?: boolean;
}

// Small helpers to keep the tree readable.
/**
 * Applies a `featureCode` binding by wrapping the page in the plan gate.
 *
 * ⚠ Done HERE rather than at each page so a binding cannot be declared and then not take effect —
 * the declaration and the enforcement are the same line.
 */
const gateNode = (node: SiteMapItem, featureCode: string, gateKey?: string): SiteMapItem => ({
  ...node,
  featureCode,
  gateKey: node.gateKey ?? gateKey,
  component: node.component ? withPlanGate(node.component, featureCode, node.gateKey ?? gateKey, node.output ? { output: true } : undefined) : node.component,
  // ⚠ CHILDREN INHERIT, so binding a module is ONE line on its parent. Add/edit/print sub-routes
  // are separate routes with their own component, and a module gated on the list page but reachable
  // through `…/add` would be a lock with a hole in it — which is worse than no lock, because it
  // looks closed.
  items: Array.isArray(node.items)
    ? // A child that declares its OWN featureCode was already wrapped by its own acc()/evt(), and
      // is left alone — that is how a sub-page belonging to a different feature stays correct.
      node.items.map((child) => (child.featureCode ? child : gateNode(child, featureCode, node.gateKey ?? gateKey)))
    : node.items,
});

const withGate = (item: SiteMapItem): SiteMapItem => {
  if (!item.featureCode) {
    return item;
  }

  return gateNode(item, item.featureCode, item.gateKey);
};

const acc = (item: SiteMapItem): SiteMapItem =>
  withGate({
    visible: true,
    scope: 'account',
    component: PlaceholderPage,
    items: [],
    ...item,
  });
const evt = (item: SiteMapItem): SiteMapItem =>
  withGate({
    visible: true,
    scope: 'event',
    component: PlaceholderPage,
    items: [],
    ...item,
  });

// ── Conditional-nav helpers ─────────────────────────────────────────────────

/** The event mode that sells tickets — gates the ticketing pages in the nav. */
const EVENT_MODE_TICKETED = 'ticketed';

/**
 * Predicate: show this item for a ticketed event — or for one that has selling data on it (ROADMAP #63: never hide a
 * page that has data; an event switched from tickets to RSVP still has its orders).
 * ⚠ `featureKnownToHaveData`, not "may have": while the answer is loading, an RSVP event keeps today's menu rather than
 * flashing four selling pages in and out.
 */
export const ticketedOnly = (ctx: NavVisibilityCtx): boolean =>
  ctx.event?.eventMode === EVENT_MODE_TICKETED || featureKnownToHaveData(ctx.features, 'tickets');

/**
 * Filters a nav TREE by each item's `visibleWhen` against `ctx`, recursing into
 * groups and pruning a group that ends up with no visible children AND no page of
 * its own. MENU-ONLY — never used for route registration (routes come from the raw
 * siteMap), so hiding here never unregisters a route. Preserves object identity
 * when nothing changed so memoized consumers don't re-render needlessly.
 */
export const filterNavByVisibility = (items: SiteMapItem[], ctx: NavVisibilityCtx): SiteMapItem[] => {
  const out: SiteMapItem[] = [];

  for (const item of items) {
    if (item.visibleWhen && !item.visibleWhen(ctx)) {
      continue;
    }

    const hasChildren = Array.isArray(item.items) && item.items.length > 0;
    const children = hasChildren ? filterNavByVisibility(item.items as SiteMapItem[], ctx) : item.items;
    const keptChildren = Array.isArray(children) && children.length > 0;

    if (ctx.crewMode) {
      // Crew mode: show a page only if its `roles` include the caller's crew role,
      // OR it's a container that still has a surviving crew-allowed VISIBLE child.
      // (Hidden drawer-stage children keep a broad role for route rendering, so they
      // must NOT keep a container the crew role can't otherwise see — count only
      // visible children.) A role-less item is a container kept via its children.
      const pageRoles = Array.isArray(item.roles) ? item.roles : [];
      const roleOk = pageRoles.length > 0 && (ctx.crewRoles ?? []).some((r) => pageRoles.includes(r));
      const keptVisibleChildren = Array.isArray(children) && children.some((c) => c.visible !== false);
      if (!roleOk && !keptVisibleChildren) {
        continue;
      }
    } else if (hasChildren && !keptChildren && !item.url) {
      // A group that had children but lost them all, and has no page of its own, is dropped.
      continue;
    }

    out.push(children === item.items ? item : { ...item, items: children });
  }

  return out;
};

// ============================================
// ACCOUNT WORKSPACE
// ============================================
const accountSiteMap: SiteMapItem[] = [
  acc({
    name: 'Home',
    subtitle: 'Your workspace at a glance',
    url: '/',
    bottomNavBar: 1,
    component: HomePage,
    roles: ACCESS.ALL,
    options: { translationKey: 'home' },
    icon: 'Home',
  }),

  // A2b #57 — NOTIFICATIONS, REBUILT AGAINST THIS PRODUCT'S OWN SCHEMA.
  //
  // ⚠⚠ THERE WAS A ROUTE HERE BEFORE AND IT WAS NOT OURS. Hidden, reached from a header bell on
  // every page, rendering PlaceholderPage — and behind it a whole data layer
  // (`UserNotificationService`, `useUserNotifications`, `UserNotificationsContext`, the unrendered
  // `Alerts` components) asking for `userNotificationList { carId car { label } }`, a query that
  // exists in ONE repo in this org and it is `carexpenses-api`. No eventsmagic subgraph has ever
  // defined it. The provider was mounted app-wide and polled it every 60 seconds for every
  // signed-in user, and NOTHING EVER FAILED LOUDLY — a query the gateway rejects looks exactly like
  // an empty inbox. All of it was deleted in #51.
  //
  // ▶ It comes back only as a WHOLE: this route, a real page, the `emNotification*` queries against
  // our own subgraph, and the bell — never half of it. `npm run check-nav` asserts the foreign
  // shape stays gone AND that this one is complete.
  // ⚠ `visible: false` — the bell is how you get here; it is not a menu destination.
  acc({
    name: 'Notifications',
    key: 'notifications',
    subtitle: 'What happened, and what still needs you',
    url: '/notifications',
    component: NotificationsPage,
    icon: 'Bell',
    visible: false,
    roles: ACCESS.ALL,
    options: { translationKey: 'notifications' },
    items: [
      // ⚠ A URL STAGE, not a useState drawer (repo standard). A reload, a shared link, or a phone
      // browser resumed from the background must reopen exactly what was open — and this one is
      // reached from a ⋯ menu, which is the easiest kind of surface to lose your place in.
      acc({
        name: 'Notification settings',
        key: 'notificationSettings',
        subtitle: 'Choose what shows up in your bell',
        url: '/notifications/settings',
        component: NotificationsPage,
        icon: 'Settings',
        visible: false,
        roles: ACCESS.ALL,
        options: { translationKey: 'notifications' },
      }),
    ],
  }),

  acc({
    name: 'Events',
    subtitle: 'Create and manage your events',
    url: '',
    bottomNavBar: 2,
    roles: ACCESS.EVENT_MANAGE,
    options: { translationKey: 'events' },
    icon: 'CalendarEvent',
    items: [
      acc({
        name: 'All Events',
        key: 'events',
        subtitle: 'Every event in your workspace',
        url: '/events/all',
        component: EventsPage,
        icon: 'CalendarEvent',
        roles: ACCESS.EVENT_MANAGE,
        options: { translationKey: 'events' },
      }),
      // The Create Event wizard — launched from a button, not a nav item, so it's
      // hidden from the menu. Its own full-screen shell replaces DashboardLayout.
      // ROADMAP #116 — Copy event from a row of All Events: a stage of that page (the list stays behind it).
      acc({
        name: 'Copy Event',
        url: '/events/all/copy/:copyId',
        component: EventsPage,
        visible: false,
        icon: 'Copy',
        roles: ACCESS.EVENT_MANAGE,
        options: { translationKey: 'events' },
      }),
      acc({
        name: 'Create Event',
        key: 'events-create',
        subtitle: 'Set up a new event',
        url: '/events/create',
        component: CreateEventPage,
        visible: false,
        icon: 'CalendarPlus',
        roles: ACCESS.EVENT_MANAGE,
        options: { translationKey: 'createEvent' },
      }),
      acc({
        name: 'Event Templates',
        subtitle: 'Reusable event blueprints',
        url: '/events/templates',
        // 🔴 NOT BUILT — hidden from the menu until it is. It falls through to
        // PlaceholderPage (`acc`/`evt` default), and a menu item that opens "Coming soon"
        // is a promise the product does not keep. ⚠ The ROUTE stays registered, so an old
        // deep link still resolves; only the nav entry goes. Nothing on the roadmap builds it yet.
        // ▶ Un-hide it in the SAME change that gives it a `component:`. `npm run check-nav`
        // fails if a VISIBLE entry lands on PlaceholderPage, so this cannot come back quietly.
        visible: false,
        icon: 'LayoutGrid',
        roles: ACCESS.EVENT_MANAGE,
        options: { translationKey: 'templates' },
      }),
      // Events on OTHER accounts that this user is crew on (docs/CREW-ACCESS.md).
      // Opening one enters that event's workspace in crew mode.
      acc({
        name: 'Crew Events',
        key: 'crew-events',
        subtitle: "Events you help run on other people's accounts",
        url: '/crew',
        component: CrewEventsPage,
        icon: 'Shield',
        roles: ACCESS.EVENT_MANAGE,
      }),
    ],
  }),

  acc({
    name: 'Audience',
    subtitle: 'Contacts, groups and memberships',
    url: '/audience/contacts',
    bottomNavBar: 3,
    // The sidebar groups these under "Audience", but the mobile tab reads as
    // "Contacts" (its landing page) — clearer at a glance.
    bottomNavBarLabel: 'Contacts',
    bottomNavBarIcon: 'AddressBook',
    roles: ACCESS.AUDIENCE,
    icon: 'Users',
    // Nav group only — NOT a route. Its landing url equals the Contacts leaf's
    // url, and the router keys routes by url (parent-first wins). Giving the
    // category a component would shadow the Contacts leaf with this node (title
    // "Audience", PlaceholderPage). Leaving it component-less lets the Contacts
    // leaf own /audience/contacts with the correct page (title "Contacts").
    component: undefined,
    items: [
      acc({
        name: 'Contacts',
        key: 'contacts',
        subtitle: 'People across all your events',
        url: '/audience/contacts',
        component: ContactsPage,
        icon: 'AddressBook',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'contacts' },
        items: [
          acc({
            name: 'Add Contact',
            url: '/audience/contacts/add',
            component: ContactsPage,
            visible: false,
            icon: 'AddressBook',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'contacts' },
          }),
          acc({
            name: 'Edit Contact',
            url: '/audience/contacts/:id/edit',
            component: ContactsPage,
            visible: false,
            icon: 'AddressBook',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'contacts' },
          }),
        ],
      }),
      //acc({ name: "Groups", subtitle: "Organize contacts into groups", url: "/audience/groups", icon: "UsersGroup", roles: ACCESS.AUDIENCE }),
      acc({
        name: 'Memberships',
        key: 'memberships',
        subtitle: 'Passes and their types',
        url: '/audience/memberships',
        component: MembershipsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'memberships',
        icon: 'Id',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'memberships' },
        items: [
          acc({
            name: 'Add Membership',
            url: '/audience/memberships/add',
            component: MembershipsPage,
            visible: false,
            icon: 'Id',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'memberships' },
          }),
          acc({
            name: 'Edit Membership',
            url: '/audience/memberships/:id/edit',
            component: MembershipsPage,
            visible: false,
            icon: 'Id',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'memberships' },
          }),
          // ROADMAP #120 — the page's actions are URL stages (they were modals opened from useState).
          acc({
            name: 'Generate Memberships',
            url: '/audience/memberships/generate',
            component: MembershipsPage,
            visible: false,
            icon: 'Sparkles',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'memberships' },
          }),
          acc({
            name: 'Import Memberships',
            url: '/audience/memberships/import',
            component: MembershipsPage,
            visible: false,
            icon: 'Upload',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'memberships' },
          }),
          acc({
            name: 'Purge Expired Memberships',
            url: '/audience/memberships/purge',
            component: MembershipsPage,
            visible: false,
            icon: 'Trash',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'memberships' },
          }),
        ],
      }),
      acc({
        name: 'Membership Types',
        key: 'membershipTypes',
        subtitle: 'Reusable pass templates',
        url: '/audience/membership-types',
        component: MembershipTypesPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'membershipTypes',
        visible: false,
        icon: 'Id',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'membershipTypes' },
        items: [
          acc({
            name: 'Add Membership Type',
            url: '/audience/membership-types/add',
            component: MembershipTypesPage,
            visible: false,
            icon: 'Id',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'membershipTypes' },
          }),
          acc({
            name: 'Edit Membership Type',
            url: '/audience/membership-types/:id/edit',
            component: MembershipTypesPage,
            visible: false,
            icon: 'Id',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'membershipTypes' },
          }),
        ],
      }),
      // ROADMAP #204 — gifts to causes, with official receipts. `commercial`, like memberships (owner, 2026-10-31).
      acc({
        name: 'Donations',
        key: 'donations',
        subtitle: 'Gifts, causes and receipts',
        url: '/audience/donations',
        component: DonationsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'donations',
        icon: 'Heart',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'donations' },
        items: [
          acc({
            name: 'Record a Gift',
            url: '/audience/donations/record',
            component: DonationsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
          acc({
            name: 'Gift',
            url: '/audience/donations/:id/view',
            component: DonationsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
        ],
      }),
      // #204 step 6a — the monthly donors; their stages: a monthly gift, and a past year's yearly receipts.
      acc({
        name: 'Monthly Donors',
        key: 'monthlyDonors',
        subtitle: 'People who give every month',
        url: '/audience/monthly-donors',
        component: MonthlyDonorsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'donations',
        visible: false,
        icon: 'Heart',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'donations' },
        items: [
          acc({
            name: 'Yearly Receipts',
            url: '/audience/monthly-donors/year-receipts',
            component: MonthlyDonorsPage,
            visible: false,
            icon: 'Receipt',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
          acc({
            name: 'Monthly Gift',
            url: '/audience/monthly-donors/:id/view',
            component: MonthlyDonorsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
        ],
      }),
      acc({
        name: 'Causes',
        key: 'causes',
        subtitle: 'What people give to',
        url: '/audience/causes',
        component: CausesPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'donations',
        visible: false,
        icon: 'Heart',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'donations' },
        items: [
          acc({
            name: 'Add Cause',
            url: '/audience/causes/add',
            component: CausesPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
          // #204 — the account's gift numbers (prefix, where they continue).
          acc({
            name: 'Gift Numbers',
            url: '/audience/causes/numbering',
            component: CausesPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
          acc({
            name: 'Edit Cause',
            url: '/audience/causes/:id/edit',
            component: CausesPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
        ],
      }),
      acc({
        name: 'Receipt Details',
        key: 'receiptDetails',
        subtitle: 'Who signs your donation receipts',
        url: '/audience/receipt-details',
        component: ReceiptDetailsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'donations',
        visible: false,
        icon: 'Receipt',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'donations' },
        items: [
          acc({
            name: 'Receipt Details for a Page',
            url: '/audience/receipt-details/page/:id',
            component: ReceiptDetailsPage,
            visible: false,
            icon: 'Receipt',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'donations' },
          }),
        ],
      }),
    ],
  }),

  acc({
    name: 'Library',
    subtitle: 'Reusable building blocks',
    url: '',
    roles: ACCESS.DIRECTORY,
    icon: 'Books',
    items: [
      acc({
        name: 'Venues',
        key: 'venues',
        subtitle: 'Your venue directory',
        url: '/library/venues',
        component: VenuesPage,
        icon: 'BuildingCommunity',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'venues' },
        items: [
          acc({
            name: 'Add Venue',
            url: '/library/venues/add',
            component: VenuesPage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          acc({
            name: 'Edit Venue',
            url: '/library/venues/:id/edit',
            component: VenuesPage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
        ],
      }),
      acc({
        name: 'Vendors',
        key: 'vendors',
        subtitle: 'Service providers you work with',
        url: '/library/vendors',
        component: VendorsPage,
        icon: 'Briefcase',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'venues' },
        items: [
          acc({
            name: 'Add Vendor',
            url: '/library/vendors/add',
            component: VendorsPage,
            visible: false,
            icon: 'Briefcase',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          acc({
            name: 'Edit Vendor',
            url: '/library/vendors/:id/edit',
            component: VendorsPage,
            visible: false,
            icon: 'Briefcase',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — a vendor's people and the documents it holds.
          acc({
            name: 'Vendor',
            url: '/library/vendors/:id',
            component: LibraryVendorPage,
            visible: false,
            icon: 'Certificate',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — a vendor's people and the documents it holds.
          acc({
            name: 'Add vendor person',
            url: '/library/vendors/:id/people/add',
            component: LibraryVendorPage,
            visible: false,
            icon: 'UserPlus',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — a vendor's people and the documents it holds.
          acc({
            name: 'Vendor person',
            url: '/library/vendors/:id/people/:personLinkId/edit',
            component: LibraryVendorPage,
            visible: false,
            icon: 'Pencil',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — edit the vendor from its own page (owner, 2026-10-20): closing returns to that page and its tab, not to the list.
          acc({
            name: 'Edit vendor profile',
            url: '/library/vendors/:id/profile/edit',
            component: LibraryVendorPage,
            visible: false,
            icon: 'Pencil',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — add a document on the vendor's own page (owner, 2026-10-20).
          acc({
            name: 'Add vendor document',
            url: '/library/vendors/:id/documents/add',
            component: LibraryVendorPage,
            visible: false,
            icon: 'Upload',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
          // #170 — a vendor's people and the documents it holds.
          acc({
            name: 'Vendor document',
            url: '/library/vendors/:id/documents/:documentId/edit',
            component: LibraryVendorPage,
            visible: false,
            icon: 'FileCertificate',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'venues' },
          }),
        ],
      }),
      acc({
        name: 'Highlights',
        key: 'highlights',
        subtitle: 'Reusable event highlights',
        url: '/library/highlights',
        component: HighlightsPage,
        icon: 'Star',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'highlights' },
        items: [
          acc({
            name: 'Add Highlight',
            url: '/library/highlights/add',
            component: HighlightsPage,
            visible: false,
            icon: 'Star',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'highlights' },
          }),
          acc({
            name: 'Edit Highlight',
            url: '/library/highlights/:id/edit',
            component: HighlightsPage,
            visible: false,
            icon: 'Star',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'highlights' },
          }),
        ],
      }),
      acc({
        name: 'Categories',
        key: 'categories',
        subtitle: 'Reusable categories',
        url: '/library/categories',
        component: CategoriesPage,
        icon: 'Tag',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'categories' },
        items: [
          acc({
            name: 'Add Category',
            url: '/library/categories/add',
            component: CategoriesPage,
            visible: false,
            icon: 'Tag',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'categories' },
          }),
          acc({
            name: 'Edit Category',
            url: '/library/categories/:id/edit',
            component: CategoriesPage,
            visible: false,
            icon: 'Tag',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'categories' },
          }),
        ],
      }),
      acc({
        name: 'Vouchers',
        key: 'vouchers',
        subtitle: 'The vouchers you issue at events',
        url: '/library/vouchers',
        component: VouchersPage,
        // Owner, 2026-10-18: vouchers, discount codes and price rules are `promotions` — Pro and up (was `commercial`).
        featureCode: FEATURE_CODES.PROMOTIONS,
        gateKey: 'vouchers',
        icon: 'RosetteDiscount',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'vouchers' },
        items: [
          acc({
            name: 'Add Voucher',
            url: '/library/vouchers/add',
            component: VouchersPage,
            visible: false,
            icon: 'RosetteDiscount',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'vouchers' },
          }),
          acc({
            name: 'Edit Voucher',
            url: '/library/vouchers/:id/edit',
            component: VouchersPage,
            visible: false,
            icon: 'RosetteDiscount',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'vouchers' },
          }),
        ],
      }),
      acc({
        name: 'Rental Assets',
        key: 'rentalAssets',
        subtitle: 'Catalog of rentable assets (vehicles, equipment, furniture)',
        url: '/library/rental-assets',
        component: RentalAssetsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'rentalAssets',
        icon: 'Package',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'rentals' },
        items: [
          acc({
            name: 'Add Rental Asset',
            url: '/library/rental-assets/add',
            component: RentalAssetsPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'rentals' },
          }),
          acc({
            name: 'Edit Rental Asset',
            url: '/library/rental-assets/:id/edit',
            component: RentalAssetsPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'rentals' },
          }),
        ],
      }),
      acc({
        name: 'Volunteer Roles',
        key: 'volunteerRoles',
        subtitle: 'Reusable volunteer roles for staffing events',
        url: '/library/volunteer-roles',
        component: VolunteerRolesPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteerRoles',
        icon: 'Users',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'volunteerRoles' },
        items: [
          acc({
            name: 'Add Volunteer Role',
            url: '/library/volunteer-roles/add',
            component: VolunteerRolesPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'volunteerRoles' },
          }),
          acc({
            name: 'Edit Volunteer Role',
            url: '/library/volunteer-roles/:id/edit',
            component: VolunteerRolesPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'volunteerRoles' },
          }),
        ],
      }),
      acc({
        name: 'Partner Packages',
        key: 'partnerPackages',
        subtitle: 'Reusable sponsor / partner / exhibitor offers for your events',
        url: '/library/partner-packages',
        component: PartnerPackagesPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'partnerPackages',
        icon: 'Award',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'partnerPackages' },
        items: [
          acc({
            name: 'Add Partner Package',
            url: '/library/partner-packages/add',
            component: PartnerPackagesPage,
            visible: false,
            icon: 'Award',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'partnerPackages' },
          }),
          acc({
            name: 'Edit Partner Package',
            url: '/library/partner-packages/:id/edit',
            component: PartnerPackagesPage,
            visible: false,
            icon: 'Award',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'partnerPackages' },
          }),
        ],
      }),
      acc({
        name: 'Artists',
        key: 'artists',
        subtitle: 'Roster of bookable acts (bands, DJs, speakers) reused across events',
        url: '/library/artists',
        component: ArtistsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'artists',
        icon: 'Music',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'artists' },
        items: [
          acc({
            name: 'Add Artist',
            url: '/library/artists/add',
            component: ArtistsPage,
            visible: false,
            icon: 'Music',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'artists' },
          }),
          acc({
            name: 'Edit Artist',
            url: '/library/artists/:id/edit',
            component: ArtistsPage,
            visible: false,
            icon: 'Music',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'artists' },
          }),
        ],
      }),
      // A2b #54 — the account's PUBLIC identities. It sits in Library rather than
      // Settings because it is the same kind of thing as a venue or a partner
      // package: account-level content you attach to an event, not a preference.
      // ⚠ Add/Edit point at OrganizerProfilePage, a SEPARATE full page — this is the
      // one Library entry whose editor is not a drawer on the list page (the form is
      // ~25 fields with a rich-text description and two image pickers).
      acc({
        name: 'Organizer Pages',
        key: 'organizer-pages',
        subtitle: 'Your public identity — logo, contact details and all your events',
        url: '/library/organizer-pages',
        component: OrganizerProfilesPage,
        icon: 'BuildingCommunity',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'organizerProfiles' },
        items: [
          acc({
            name: 'Add Organizer Page',
            url: '/library/organizer-pages/add',
            component: OrganizerProfilePage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
          acc({
            // #288 — a page with subscriptions still running: stop them before it can be removed.
            name: 'Stop Organizer Page Subscriptions',
            url: '/library/organizer-pages/:id/stop-subscriptions',
            component: OrganizerProfilesPage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
          acc({
            name: 'Edit Organizer Page',
            url: '/library/organizer-pages/:id/edit',
            component: OrganizerProfilePage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
          acc({
            // B20 round 2 — the page's theme, look and background pictures (like an event's Website theme).
            name: 'Organizer Page Theme',
            url: '/library/organizer-pages/:id/theme',
            component: OrganizerProfileThemePage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
          acc({
            // …its full-screen preview (a drawer stage over the theme page).
            name: 'Organizer Page Theme Preview',
            url: '/library/organizer-pages/:id/theme/preview',
            component: OrganizerProfileThemePage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
          acc({
            // B24.5 — who follows the page by email (a drawer stage over the editor).
            name: 'Organizer Page Followers',
            url: '/library/organizer-pages/:id/followers',
            component: OrganizerProfilePage,
            visible: false,
            icon: 'BuildingCommunity',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'organizerProfiles' },
          }),
        ],
      }),
      acc({
        name: 'Marketing Channels',
        key: 'marketing-channels',
        subtitle: 'Traffic-source markers (?ch=) reused across all events',
        url: '/library/marketing-channels',
        component: MarketingChannelsPage,
        // Every plan since 2026-10-22 (owner — docs/COMPETITIVE-BASELINE.md in eventsmagic-api): no plan lock.
        icon: 'Speakerphone',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'marketingChannels' },
        items: [
          acc({
            name: 'Add Marketing Channel',
            url: '/library/marketing-channels/add',
            component: MarketingChannelsPage,
            visible: false,
            icon: 'Speakerphone',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'marketingChannels' },
          }),
          acc({
            name: 'Edit Marketing Channel',
            url: '/library/marketing-channels/:id/edit',
            component: MarketingChannelsPage,
            visible: false,
            icon: 'Speakerphone',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'marketingChannels' },
          }),
        ],
      }),
      acc({
        name: 'Storage',
        key: 'storage',
        subtitle: 'All your uploaded files',
        url: '/library/storage',
        component: StoragePage,
        icon: 'Database',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'storage' },
      }),
      // ROADMAP #130 — Upload, a file's Edit drawer and its image editor are URL stages (they were dialogs on useState).
      acc({ name: 'Upload Files', url: '/library/storage/upload', component: StoragePage, visible: false, icon: 'Upload', roles: ACCESS.DIRECTORY }),
      acc({ name: 'Edit File', url: '/library/storage/:fileId/edit', component: StoragePage, visible: false, icon: 'Edit', roles: ACCESS.DIRECTORY }),
      acc({ name: 'Edit Image', url: '/library/storage/:fileId/edit-image', component: StoragePage, visible: false, icon: 'Photo', roles: ACCESS.DIRECTORY }),
      // ⚠ TAGS LIVE IN THE LIBRARY, not under Audience, because they are not an audience thing:
      // `person_tag` AND `note_tag` both exist, so a tag already labels people and records. Its twin
      // — Categories, the account-wide vocabulary behind every picker — was already here, and one
      // vocabulary in Audience with its twin in Library is the split that makes people hunt.
      acc({
        name: 'Tags',
        key: 'tags',
        subtitle: 'Labels for people and records',
        url: '/library/tags',
        component: TagsPage,
        icon: 'Tag',
        roles: ACCESS.AUDIENCE,
        options: { translationKey: 'tags' },
        items: [
          acc({
            name: 'Add Tag',
            url: '/library/tags/add',
            component: TagsPage,
            visible: false,
            icon: 'Tag',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'tags' },
          }),
          acc({
            name: 'Edit Tag',
            url: '/library/tags/:id/edit',
            component: TagsPage,
            visible: false,
            icon: 'Tag',
            roles: ACCESS.AUDIENCE,
            options: { translationKey: 'tags' },
          }),
        ],
      }),
      // ⚠ POLICIES ARE A LIBRARY CATALOG, not a setting. You author one and attach it to events
      // (`EventPolicyCore`) — the same shape as Venues, Vouchers, Partner Packages and every other
      // entry here. Settings is for how the account BEHAVES, not for things you make.
      // ⓘ Its `key` is what moved the url for free: `getRoutePath('policies')` needed no edit.
      acc({
        name: 'Legal Policies',
        key: 'policies',
        subtitle: 'Terms, waivers and consents',
        url: '/library/policies',
        component: PoliciesPage,
        featureCode: FEATURE_CODES.PRO_REGISTRATION,
        gateKey: 'policies',
        icon: 'Gavel',
        roles: ACCESS.ACCOUNT_ADMIN,
        options: { translationKey: 'policies' },
        items: [
          acc({
            name: 'Add Policy',
            url: '/library/policies/add',
            component: PoliciesPage,
            visible: false,
            icon: 'Gavel',
            roles: ACCESS.ACCOUNT_ADMIN,
            options: { translationKey: 'policies' },
          }),
          acc({
            name: 'Edit Policy',
            url: '/library/policies/:id/edit',
            component: PoliciesPage,
            visible: false,
            icon: 'Gavel',
            roles: ACCESS.ACCOUNT_ADMIN,
            options: { translationKey: 'policies' },
          }),
        ],
      }),
      // ROADMAP A2e #89 — the theme library: the defaults and the account's own themes, duplicate / edit / remove. The
      // editor is its own PAGE (a long form with a live preview), not a drawer; "New theme" is a drawer stage that
      // duplicates a starting theme and opens the editor. Own themes are premium (`custom_themes`, gate 'add' — the
      // page works on every plan; ThemeCore refuses create / duplicate / edit). ⓘ Since #97 that feature is advanced styling
      // (`site_styling`), which also covers the account's custom CSS (/library/themes/css).
      acc({
        name: 'Themes',
        key: 'themes',
        subtitle: 'Visual themes for event pages',
        url: '/library/themes',
        component: ThemesPage,
        icon: 'Palette',
        roles: ACCESS.DESIGN,
        options: { translationKey: 'themes' },
        items: [
          // A2e #97 — the account's custom CSS (every event website): a drawer stage of the theme library.
          acc({
            name: 'Custom CSS',
            url: '/library/themes/css',
            component: ThemesPage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DESIGN,
            options: { translationKey: 'themes' },
          }),
          acc({
            name: 'New Theme',
            url: '/library/themes/add',
            component: ThemesPage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DESIGN,
            options: { translationKey: 'themes' },
          }),
          acc({
            name: 'Edit Theme',
            key: 'themeEdit',
            url: '/library/themes/:id/edit',
            component: ThemeEditorPage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DESIGN,
            options: { translationKey: 'themes' },
          }),
          // The editor's full-screen preview — a drawer, so a URL stage of the SAME page (unsaved edits stay).
          acc({
            name: 'Preview Theme',
            url: '/library/themes/:id/edit/preview',
            component: ThemeEditorPage,
            visible: false,
            icon: 'Palette',
            roles: ACCESS.DESIGN,
            options: { translationKey: 'themes' },
          }),
        ],
      }),
    ],
  }),

  // Inventory is a TOP-LEVEL group (not nested under Library) so it stays a
  // 2-level menu: the mobile "More" view only renders top-level groups + their
  // direct children, so a group-inside-a-group (Library › Inventory › pages) is
  // an unreachable dead tap on mobile. Keeping Inventory at the top level makes
  // its pages tappable AND searchable in the More view. Catalog + home stock.
  // ⚠ ACCOUNT-LEVEL Analytics — distinct from the per-EVENT Analytics group further down. This one
  // answers questions that only make sense ACROSS events; a marketing channel is defined once per
  // account and reused everywhere precisely so "which poster works" can be asked once.
  // ⓘ It starts with one item on purpose (owner, 2026-09-28). ROADMAP B1 (the host-facing Reports
  // module) lands here rather than inventing a second home for reports later.
  acc({
    name: 'Analytics',
    subtitle: 'What is working, across all your events',
    url: '',
    roles: ACCESS.DIRECTORY,
    icon: 'ChartBar',
    items: [
      acc({
        // ⚠ Named for what it ANSWERS, not for the feature it reads from (owner, 2026-09-28).
        // "Marketing" beside "Marketing Channels" in the Library read as the same thing twice; the
        // channels are still named, in the subtitle, so the connection is not lost.
        name: 'Traffic and conversions',
        key: 'marketing-report',
        subtitle: 'Which marketing channels bring people to your events',
        url: '/analytics/marketing',
        component: MarketingReportPage,
        // The SAME as the channels it reports on — every plan since 2026-10-22 (owner), so no plan lock.
        icon: 'Speakerphone',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'marketingChannels' },
      }),
    ],
  }),

  acc({
    name: 'Inventory',
    subtitle: 'Catalog + home stock (receive, transfer, count, consume)',
    url: '',
    icon: 'Packages',
    roles: ACCESS.DIRECTORY,
    options: { translationKey: 'inventory' },
    items: [
      acc({
        name: 'Inventory Items',
        key: 'inventoryItems',
        subtitle: 'Catalog of stock-tracked supplies and gear',
        url: '/library/inventory-items',
        component: InventoryItemsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'inventory',
        icon: 'Packages',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'inventory' },
        items: [
          acc({
            name: 'Add Inventory Item',
            url: '/library/inventory-items/add',
            component: InventoryItemsPage,
            visible: false,
            icon: 'Packages',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'inventory' },
          }),
          acc({
            name: 'Edit Inventory Item',
            url: '/library/inventory-items/:id/edit',
            component: InventoryItemsPage,
            visible: false,
            icon: 'Packages',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'inventory' },
          }),
          acc({
            name: 'Print Inventory Labels',
            url: '/library/inventory-items/labels',
            component: InventoryLabelsPrintPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'inventory' },
          }),
        ],
      }),
      acc({
        name: 'Stock',
        key: 'inventoryStock',
        subtitle: 'All stock on hand — home and every event',
        url: '/library/inventory/stock',
        component: InventoryStockPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'inventoryStock',
        icon: 'Package',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'inventory' },
        items: [
          // ROADMAP #120 — "Write off stock" is a URL stage (it was a modal opened from useState). A child, so it takes
          // the page's plan gate.
          acc({
            name: 'Write Off Stock',
            url: '/library/inventory/stock/:id/write-off',
            component: InventoryStockPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'inventory' },
          }),
        ],
      }),
      acc({
        name: 'Operations',
        key: 'inventoryOperations',
        subtitle: 'Receive, transfer, count and consume',
        url: '/library/inventory/operations',
        component: InventoryOperationsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'inventoryOps',
        icon: 'Scan',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'inventory' },
      }),
      acc({
        name: 'History',
        key: 'inventoryHistory',
        subtitle: 'Stock movement ledger',
        url: '/library/inventory/history',
        component: InventoryHistoryPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'inventoryHistory',
        icon: 'History',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'inventory' },
      }),
      acc({
        name: 'Purchase Orders',
        key: 'purchaseOrders',
        subtitle: 'Restock orders to your vendors — printable buy-lists',
        url: '/library/purchase-orders',
        component: PurchaseOrdersPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'purchaseOrders',
        icon: 'ClipboardList',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'purchaseOrders' },
        items: [
          acc({
            name: 'New Purchase Order',
            url: '/library/purchase-orders/add',
            component: PurchaseOrdersPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'purchaseOrders' },
          }),
          acc({
            name: 'Edit Purchase Order',
            url: '/library/purchase-orders/:id/edit',
            component: PurchaseOrdersPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'purchaseOrders' },
          }),
          acc({
            name: 'Purchase Order Buy List',
            url: '/library/purchase-orders/:id/buy-list',
            component: PurchaseOrdersPage,
            visible: false,
            icon: 'ClipboardCheck',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'purchaseOrders' },
          }),
          acc({
            name: 'Print Purchase Order',
            url: '/library/purchase-orders/:id/print',
            component: PurchaseOrderPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.DIRECTORY,
            options: { translationKey: 'purchaseOrders' },
          }),
        ],
      }),
      acc({
        name: 'Inventory',
        url: '/library/inventory',
        component: InventoryRedirect,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'inventoryStock',
        visible: false,
        icon: 'Packages',
        roles: ACCESS.DIRECTORY,
        options: { translationKey: 'inventory' },
      }),
    ],
  }),

  acc({
    name: 'Money',
    subtitle: 'Payments, taxes and billing',
    // ⚠ Same shape as Overview / Audience / Guests: the Payments leaf below owns this url, so the
    // group must not also claim it. Two nodes on one url are two <Route> children sharing one React
    // key, and which one renders is decided by registration order. Guarded by `npm run check-nav`.
    url: '/money/payments',
    component: undefined,
    roles: ACCESS.FINANCE,
    icon: 'CreditCard',
    items: [
      acc({
        name: 'Payments',
        subtitle: 'Transactions and payouts',
        url: '/money/payments',
        component: PaymentsPage,
        icon: 'CreditCard',
        roles: ACCESS.FINANCE,
        items: [
          acc({
            name: 'Transaction',
            url: '/money/payments/:paymentId',
            component: PaymentsPage,
            visible: false,
            icon: 'CreditCard',
            roles: ACCESS.FINANCE,
          }),
          // The printable receipt for money the host paid US (A0.1.2). Account-scoped rather than
          // event-scoped, because an account-wide top-up has no event — which is also why it is a
          // separate route from the event-scoped ticket receipt.
          acc({
            name: 'Print Purchase Receipt',
            url: '/money/payments/:paymentId/spending-receipt-print',
            component: SpendingReceiptPrintPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.FINANCE,
          }),
        ],
      }),
      // ⚠ Un-hidden in the SAME change that gave it a page — ROADMAP A2b #52a. Everything else
      // about tax (the table, the pricing engine, the per-ticket-type override, the tax line on
      // five receipts) has existed for months; without this page there was no way to CREATE a rate,
      // so none of it could ever fire.
      acc({
        name: 'Taxes',
        key: 'tax-settings',
        subtitle: 'Tax rates you charge on tickets',
        url: '/money/tax-settings',
        component: TaxSettingsPage,
        icon: 'ReceiptTax',
        roles: ACCESS.FINANCE,
        items: [
          acc({
            name: 'Add Tax Rate',
            url: '/money/tax-settings/add',
            component: TaxSettingsPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.FINANCE,
          }),
          acc({
            name: 'Edit Tax Rate',
            url: '/money/tax-settings/:id/edit',
            component: TaxSettingsPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.FINANCE,
          }),
        ],
      }),
      acc({
        name: 'Get paid',
        // ⚠ `key` matters: without one `getRoutePath('stripeConnect')` silently returns '/'.
        // The burst-cap notice in the message composer sends a host here to lift their send
        // limit, and a CTA that lands on the dashboard is worse than no CTA. (The same
        // omission on 'subscription' was found and fixed the same way.)
        key: 'stripeConnect',
        subtitle: 'Connect Stripe to receive ticket payments',
        url: '/money/stripe-connect',
        component: StripeConnectPage,
        icon: 'CreditCard',
        roles: ACCESS.BILLING,
      }),
      acc({
        name: 'Subscription',
        key: 'subscription',
        subtitle: 'Your plan and billing',
        url: '/money/subscription',
        component: SubscriptionPage,
        icon: 'Crown',
        roles: ACCESS.BILLING,
        options: { translationKey: 'subscription' },
      }),
    ],
  }),

  acc({
    name: 'Settings',
    subtitle: 'Account, branding and legal',
    url: '',
    roles: ACCESS.ACCOUNT_ADMIN,
    icon: 'Settings',
    items: [
      acc({
        name: 'Account Settings',
        key: 'accountSettings',
        subtitle: 'Workspace configuration',
        url: '/settings/account',
        component: AccountSettingsPage,
        icon: 'Settings',
        // The account's settings and closing it are the OWNER's (owner, 2026-10-16) — ms_auth enforces it.
        roles: ACCESS.ACCOUNT_OWNER,
        options: { translationKey: 'accountSettings' },
        items: [
          // Close the account — IRREVERSIBLE (owner, 2026-10-14; ms_auth `accountClose`): a drawer stage of the page.
          acc({
            name: 'Close Account',
            url: '/settings/account/close',
            component: AccountSettingsPage,
            visible: false,
            icon: 'Settings',
            roles: ACCESS.ACCOUNT_OWNER,
            options: { translationKey: 'accountSettings' },
          }),
        ],
      }),
      acc({
        name: 'Organizer Profile',
        subtitle: 'Your public organizer page',
        url: '/settings/organizer-profile',
        // 🔴 NOT BUILT — hidden from the menu until it is. It falls through to
        // PlaceholderPage (`acc`/`evt` default), and a menu item that opens "Coming soon"
        // is a promise the product does not keep. ⚠ The ROUTE stays registered, so an old
        // deep link still resolves; only the nav entry goes. This is ROADMAP A2b #54 — organizer profile.
        // ▶ Un-hide it in the SAME change that gives it a `component:`. `npm run check-nav`
        // fails if a VISIBLE entry lands on PlaceholderPage, so this cannot come back quietly.
        visible: false,
        icon: 'UserCircle',
        roles: ACCESS.ACCOUNT_ADMIN,
      }),
      acc({
        name: 'White Label',
        subtitle: 'Custom domain and branding',
        url: '/settings/white-label',
        // 🔴 NOT BUILT — hidden from the menu until it is. It falls through to
        // PlaceholderPage (`acc`/`evt` default), and a menu item that opens "Coming soon"
        // is a promise the product does not keep. ⚠ The ROUTE stays registered, so an old
        // deep link still resolves; only the nav entry goes. The `white_label` table exists; the page that edits it does not.
        // ▶ Un-hide it in the SAME change that gives it a `component:`. `npm run check-nav`
        // fails if a VISIBLE entry lands on PlaceholderPage, so this cannot come back quietly.
        visible: false,
        icon: 'Wand',
        roles: ACCESS.ACCOUNT_ADMIN,
      }),
      acc({
        // A2b #53 — what a NEW event starts from. ⚠ Named for the job, not the mechanism: the old
        // label "Preferences" (subtitled "Language, currency, notifications") described a page that
        // held none of those things and could not save the one it did show — it queried
        // carexpenses-api's `userProfileList`, which no eventsmagic subgraph has ever defined.
        key: 'event-defaults',
        name: 'Event defaults',
        subtitle: 'Currency, place and guest settings new events start from',
        url: '/settings/preferences',
        component: EventDefaultsPage,
        icon: 'Adjustments',
        roles: ACCESS.ALL,
        options: { translationKey: 'eventDefaults' },
        items: [
          // ROADMAP #190 — the dashboard cards every event starts with (a drawer over this page).
          acc({
            name: 'Dashboard Cards',
            url: '/settings/preferences/dashboard',
            component: EventDefaultsPage,
            visible: false,
            icon: 'LayoutDashboard',
            roles: ACCESS.ALL,
            options: { translationKey: 'eventDefaults' },
          }),
        ],
      }),
      acc({
        name: 'Profile',
        subtitle: 'Your personal profile',
        key: 'profile',
        url: '/settings/profile',
        component: UserProfilePage,
        icon: 'User',
        visible: false,
        roles: ACCESS.ALL,
        options: { translationKey: 'userProfilePage' },
        items: [
          // "Ask about my data" — a privacy request about the signed-in user's own address (owner, 2026-10-14).
          acc({
            name: 'Ask About My Data',
            url: '/settings/profile/privacy',
            component: UserProfilePage,
            visible: false,
            icon: 'User',
            roles: ACCESS.ALL,
            options: { translationKey: 'userProfilePage' },
          }),
        ],
      }),
    ],
  }),

  acc({
    name: 'Team',
    subtitle: 'Users and invitations',
    url: '',
    roles: ACCESS.ACCOUNT_ADMIN,
    icon: 'UsersGroup',
    items: [
      acc({
        name: 'Users',
        subtitle: 'Manage workspace users',
        url: '/team/users',
        component: UsersPage,
        icon: 'Users',
        roles: ACCESS.ACCOUNT_ADMIN,
        options: { translationKey: 'users' },
        items: [
          acc({
            name: 'Edit User',
            url: '/team/users/:id/edit',
            component: UsersPage,
            visible: false,
            icon: 'User',
            roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
            options: { translationKey: 'users' },
          }),
        ],
      }),
      acc({
        name: 'Invitations',
        subtitle: 'Invite people to your team',
        url: '/team/invitations',
        component: UserInvitationsPage,
        icon: 'UserPlus',
        roles: ACCESS.ACCOUNT_ADMIN,
        options: { translationKey: 'userInvitations' },
        items: [
          acc({
            name: 'Invite User',
            url: '/team/invitations/add',
            component: UserInvitationsPage,
            visible: false,
            icon: 'UserPlus',
            roles: ACCESS.ACCOUNT_ADMIN,
            options: { translationKey: 'userInvitations' },
          }),
          acc({
            name: 'Edit Invitation',
            url: '/team/invitations/:id/edit',
            component: UserInvitationsPage,
            visible: false,
            icon: 'User',
            roles: ACCESS.ACCOUNT_ADMIN,
            options: { translationKey: 'userInvitations' },
          }),
        ],
      }),
    ],
  }),
];

// ============================================
// EVENT WORKSPACE ( /events/:eventId/... )
// ============================================
const eventSiteMap: SiteMapItem[] = [
  evt({
    name: 'Overview',
    subtitle: 'This event at a glance',
    url: '/events/:eventId',
    bottomNavBar: 1,
    roles: ACCESS.EVENT_VIEW,
    icon: 'LayoutDashboard',
    // Nav group only — the Dashboard leaf below owns /events/:eventId (same url).
    // Leaving this component-less lets the leaf's real page win over PlaceholderPage.
    component: undefined,
    items: [
      evt({
        name: 'Dashboard',
        subtitle: 'Sales, RSVPs and attendance',
        url: '/events/:eventId',
        component: EventDashboardPage,
        icon: 'LayoutDashboard',
        roles: ACCESS.EVENT_VIEW,
      }),
      evt({
        name: 'Guest Stats',
        url: '/events/:eventId/stats',
        component: EventDashboardPage,
        visible: false,
        icon: 'ChartBar',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #120 — the plans chooser and Cancel event are URL stages (they were modals opened from useState). Same
      // roles as the dashboard whose menu opens them: the stage must not be stricter or looser than the button.
      evt({
        name: 'Event Plans',
        url: '/events/:eventId/plans',
        component: EventDashboardPage,
        visible: false,
        icon: 'Rocket',
        roles: ACCESS.EVENT_VIEW,
      }),
      evt({
        name: 'Cancel Event',
        url: '/events/:eventId/cancel',
        component: EventDashboardPage,
        visible: false,
        icon: 'X',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #116 — Copy event, a stage of the dashboard. The page leaves it for whoever `canCopyEvent` refuses.
      evt({
        name: 'Copy Event',
        url: '/events/:eventId/copy',
        component: EventDashboardPage,
        visible: false,
        icon: 'Copy',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #185 a — Sell at the door from the dashboard: the Box Office's own drawer as a stage of the dashboard (the
      // host stays on the page they sell from). The page leaves it for whoever `canSellAtTheDoor` refuses.
      evt({
        name: 'Sell Tickets',
        url: '/events/:eventId/sell',
        component: EventDashboardPage,
        visible: false,
        icon: 'Ticket',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #197 — sell a partner package from the dashboard (the Sell menu's second entry). The page leaves it for
      // whoever `canSellPartnerPackages` refuses, and on an event that offers no package.
      evt({
        name: 'Sell Package',
        url: '/events/:eventId/sell/package',
        component: EventDashboardPage,
        visible: false,
        icon: 'Building',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #188 — the test records (see / clear), from the test-mode banner. The page leaves it for whoever may not
      // change the event's settings.
      evt({
        name: 'Test Records',
        url: '/events/:eventId/test-records',
        component: EventDashboardPage,
        visible: false,
        icon: 'List',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #190 — choose, hide and order the dashboard's cards (the ⋯ menu); the page leaves it for crew who may not
      // change the event's settings.
      evt({
        name: 'Customize Dashboard',
        url: '/events/:eventId/customize',
        component: EventDashboardPage,
        visible: false,
        icon: 'LayoutDashboard',
        roles: ACCESS.EVENT_VIEW,
      }),
      // ROADMAP #130 — Share (the link + QR) is a URL stage too; it was a modal on `useState`.
      evt({
        name: 'Share Event',
        url: '/events/:eventId/share',
        component: EventDashboardPage,
        visible: false,
        icon: 'Send',
        roles: ACCESS.EVENT_VIEW,
      }),
      evt({
        name: 'Details & Settings',
        subtitle: 'Event name, date, venue',
        url: '/events/:eventId/details',
        component: EventDetailsPage,
        icon: 'Settings',
        roles: ACCESS.EVENT_ADMIN,
      }),
      // ROADMAP #130 — Event details' Cancel opens the cancel drawer on its own stage (same roles as the page).
      evt({
        name: 'Cancel Event (details)',
        url: '/events/:eventId/details/cancel',
        component: EventDetailsPage,
        visible: false,
        icon: 'X',
        roles: ACCESS.EVENT_ADMIN,
      }),
      // ROADMAP #188 — Details & Settings' test records (the current run's, or the ones hidden since it went live).
      evt({
        name: 'Test Records (details)',
        url: '/events/:eventId/details/test-records',
        component: EventDetailsPage,
        visible: false,
        icon: 'List',
        roles: ACCESS.EVENT_ADMIN,
      }),
      // ROADMAP A2b #63 P5 — what this event uses. The re-entry point for the wizard's Features step: the same cards,
      // and what is ticked here decides what the menu shows (the rest moves behind "Show more", never away).
      evt({
        name: 'Event features',
        key: 'eventFeatures',
        subtitle: 'What this event uses — and what its menu shows',
        url: '/events/:eventId/features',
        component: EventFeaturesPage,
        icon: 'LayoutGrid',
        roles: ACCESS.EVENT_ADMIN,
      }),
      evt({
        name: 'Schedule',
        subtitle: 'Dates, occurrences and sub-events',
        url: '/events/:eventId/schedule',
        // ⓘ Owner, 2026-10-01: Sessions is a feature, and this is its page — kept in Overview, where hosts know it. A
        //   repeating event keeps it in the menu whatever the switch says: its generated dates count as data.
        eventFeature: 'sessions',
        component: EventSchedulePage,
        icon: 'CalendarTime',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Add / edit stages for a time slot — same page, drawer driven by the URL.
          evt({
            name: 'Add Slot',
            url: '/events/:eventId/schedule/add',
            component: EventSchedulePage,
            visible: false,
            icon: 'CalendarTime',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Slot',
            url: '/events/:eventId/schedule/:id/edit',
            component: EventSchedulePage,
            visible: false,
            icon: 'CalendarTime',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // #199 — cancel ONE date (who is told, optional refunds); on a cancelled date, its refund progress.
          evt({
            name: 'Cancel Date',
            url: '/events/:eventId/schedule/:id/cancel',
            component: EventSchedulePage,
            visible: false,
            icon: 'CalendarMinus',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // GUESTS — everyone attending, plus day-of check-in and segmentation.
  evt({
    name: 'Guests',
    subtitle: 'Guest list, crew, groups and check-in',
    // ⚠ KEEPS its `url` — `bottomNavBar` reads it for the phone tab — but NOT a component. Same
    // shape as Overview and Audience above: the Guest List leaf below owns the identical url, and a
    // component here would shadow it (parent-first wins), so the page rendered under the parent's
    // title "Guests" instead of the leaf's "Guest List". It used to hold EventGuestsPage while the
    // leaf fell through to PlaceholderPage — correct only by registration order, which is not a
    // thing to depend on. Guarded by `npm run check-nav`.
    url: '/events/:eventId/guests',
    component: undefined,
    bottomNavBar: 2,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'Users',
    items: [
      evt({
        name: 'Guest List',
        subtitle: 'Everyone attending',
        url: '/events/:eventId/guests',
        component: EventGuestsPage,
        icon: 'Users',
        roles: ACCESS.EVENT_GUESTS,
      }),
      // Add / edit stages for a guest — same page, drawer driven by the URL.
      evt({
        name: 'Add Guest',
        url: '/events/:eventId/guests/add',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Edit Guest',
        url: '/events/:eventId/guests/:id/edit',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Guest Answers',
        url: '/events/:eventId/guests/:id/answers',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Guest Notes',
        url: '/events/:eventId/guests/:id/notes',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ROADMAP #115 — the BIB drawers are URL stages (Generate is the guest list's only; a crew row has Assign too).
      evt({
        name: 'Assign Guest BIB',
        url: '/events/:eventId/guests/:id/bib',
        component: EventGuestsPage,
        visible: false,
        icon: 'Hash',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // #172 follow-up c — give this person a QR tag (the door's step, for one).
      evt({
        name: 'Give Guest QR Tag',
        url: '/events/:eventId/guests/:id/qr-tag',
        component: EventGuestsPage,
        visible: false,
        icon: 'Qrcode',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Generate Guest BIBs',
        url: '/events/:eventId/guests/bibs',
        component: EventGuestsPage,
        visible: false,
        icon: 'Hash',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ROADMAP #120 — renaming an RSVP group is a URL stage (it was a modal opened from useState). `:groupId`, not `:id`.
      evt({
        name: 'Rename RSVP Group',
        url: '/events/:eventId/guests/rsvp-groups/:groupId/rename',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Review Guest Request',
        url: '/events/:eventId/guests/:id/review',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Review Selected Guest Requests',
        url: '/events/:eventId/guests/review-selected',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Add Guests From Contacts',
        url: '/events/:eventId/guests/add-from-contacts',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Import Guests',
        url: '/events/:eventId/guests/import',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Print Guests',
        url: '/events/:eventId/guests/print',
        component: EventGuestsPage,
        visible: false,
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ⚠ The SHEET itself, opened in a new tab by the chooser above. Its layout, group set and the
      // guest filter travel as query params — see GuestListPrintPage.
      evt({
        name: 'Guest List Sheet',
        url: '/events/:eventId/guests/print/sheet',
        component: GuestListPrintPage,
        visible: false,
        icon: 'Printer',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ⚠ Name badges (B2 #18). A SECOND sheet off the same chooser, not a second chooser — the guest
      // filter, the RSVP narrowing and the group narrowing are the same decisions for both.
      evt({
        name: 'Guest Badges Sheet',
        url: '/events/:eventId/guests/badges/print/sheet',
        component: EventBadgesPrintPage,
        visible: false,
        icon: 'Printer',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Guest List Stats',
        url: '/events/:eventId/guests/stats',
        component: EventGuestsPage,
        visible: false,
        icon: 'ChartBar',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ROADMAP #177 — who has arrived, who is still here, who left; the guest's drawers are URL stages of this page too.
      evt({
        name: 'Arrivals',
        subtitle: 'Who has arrived, who is still here and who left',
        url: '/events/:eventId/arrivals',
        component: EventArrivalsPage,
        icon: 'Login',
        roles: ACCESS.EVENT_GUESTS,
        items: [
          evt({
            name: 'Edit Arriving Guest',
            url: '/events/:eventId/arrivals/:id/edit',
            component: EventArrivalsPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Arriving Guest Answers',
            url: '/events/:eventId/arrivals/:id/answers',
            component: EventArrivalsPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Arriving Guest Notes',
            url: '/events/:eventId/arrivals/:id/notes',
            component: EventArrivalsPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Assign Arriving Guest BIB',
            url: '/events/:eventId/arrivals/:id/bib',
            component: EventArrivalsPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // #172 follow-up c — give this person a QR tag (the door's step, for one).
          evt({
            name: 'Give Arriving Guest QR Tag',
            url: '/events/:eventId/arrivals/:id/qr-tag',
            component: EventArrivalsPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Check-in',
        subtitle: 'Scan and check guests in and out at the door',
        url: '/events/:eventId/checkin',
        component: EventCheckinPage,
        icon: 'UserCheck',
        roles: ACCESS.EVENT_CHECKIN,
        items: [
          // ROADMAP #171 — give the scanned person's party their BIB numbers (preprinted bibs handed out at the door). A
          // URL stage, so a reload or a phone resumed from the background reopens it; the console reads the person by id.
          evt({
            name: 'Assign BIB numbers',
            url: '/events/:eventId/checkin/bibs/:subjectId',
            component: EventCheckinPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.EVENT_CHECKIN,
          }),
          // #172 — the door hands a party their QR tags (the BIB step's twin).
          evt({
            name: 'Give QR tags',
            url: '/events/:eventId/checkin/tags/:subjectId',
            component: EventCheckinPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_CHECKIN,
          }),
          // #172 — the person's answers to the registration questions (read-only for crew; the owner may edit them).
          evt({
            name: 'Registration answers',
            url: '/events/:eventId/checkin/answers/:subjectId',
            component: EventCheckinPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_CHECKIN,
          }),
          // ROADMAP #169 — the event's entrances & desks (venue points listed, the event's own edited), opened from the
          // console's "You are at" picker. A URL stage, so a reload reopens it.
          evt({
            name: 'Entrances & service points',
            url: '/events/:eventId/checkin/points',
            component: EventCheckinPage,
            visible: false,
            icon: 'MapPin',
            roles: ACCESS.EVENT_CHECKIN,
          }),
        ],
      }),
      evt({
        name: 'Crew',
        subtitle: 'Co-hosts, staff, volunteers, speakers',
        url: '/events/:eventId/crew',
        component: EventCrewPage,
        icon: 'Shield',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Crew',
            url: '/events/:eventId/crew/add',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Crew',
            url: '/events/:eventId/crew/:id/edit',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Crew Answers',
            url: '/events/:eventId/crew/:id/answers',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Crew Notes',
            url: '/events/:eventId/crew/:id/notes',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Assign Crew BIB',
            url: '/events/:eventId/crew/:id/bib',
            component: EventCrewPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // #172 follow-up c — give this person a QR tag (the door's step, for one).
          evt({
            name: 'Give Crew QR Tag',
            url: '/events/:eventId/crew/:id/qr-tag',
            component: EventCrewPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — the crew list renders the same page, with the same "By RSVP group" view.
          evt({
            name: 'Rename RSVP Group',
            url: '/events/:eventId/crew/rsvp-groups/:groupId/rename',
            component: EventCrewPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add Crew From Contacts',
            url: '/events/:eventId/crew/add-from-contacts',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #38 — its own URL stage, so the picker survives a reload and a phone resumed
          // from the background, exactly like add-from-contacts beside it.
          evt({
            name: 'Add Crew From Account Users',
            url: '/events/:eventId/crew/add-from-users',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Import Crew',
            url: '/events/:eventId/crew/import',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Crew List Stats',
            url: '/events/:eventId/crew/stats',
            component: EventCrewPage,
            visible: false,
            icon: 'ChartBar',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Crew',
            url: '/events/:eventId/crew/print',
            component: EventCrewPage,
            visible: false,
            icon: 'Shield',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Crew List Sheet',
            url: '/events/:eventId/crew/print/sheet',
            component: GuestListPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ The crew mirror. `GuestPrintDrawer` builds its URL from `routeSegment`, so a crew badge
          // link is a live route the moment the drawer is opened from the crew list — and a URL stage
          // that is not in the sitemap is a 404 nobody notices until somebody clicks it.
          evt({
            name: 'Crew Badges Sheet',
            url: '/events/:eventId/crew/badges/print/sheet',
            component: EventBadgesPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ Everyone connected to this event who might need phoning, on ONE sheet — for when somebody
          // has not arrived, everyone is on site and there is no internet. Crew-only: private numbers.
          evt({
            name: 'Call Sheet',
            url: '/events/:eventId/call-sheet/print',
            component: EventCallSheetPrintPage,
            visible: false,
            icon: 'Phone',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Guest Groups',
        subtitle: 'Tables, teams and segments',
        url: '/events/:eventId/guests/groups',
        component: EventGuestGroupsPage,
        icon: 'UsersGroup',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Add / edit a group set + manage the groups inside it — same page, URL-driven.
          evt({
            name: 'Add Group Set',
            url: '/events/:eventId/guests/groups/add',
            component: EventGuestGroupsPage,
            visible: false,
            icon: 'UsersGroup',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Group Set',
            url: '/events/:eventId/guests/groups/:id/edit',
            component: EventGuestGroupsPage,
            visible: false,
            icon: 'UsersGroup',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Manage Groups',
            url: '/events/:eventId/guests/groups/:id/manage',
            component: EventGuestGroupsPage,
            visible: false,
            icon: 'UsersGroup',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // REGISTRATION — how guests sign up & pay. The ticketing pages appear only for a
  // ticketed event (visibleWhen: ticketedOnly); their routes stay registered so
  // deep links still resolve (a future upgrade gate can render there instead).
  evt({
    name: 'Registration',
    subtitle: 'RSVP questions, tickets and discounts',
    url: '',
    bottomNavBar: 3,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'ClipboardList',
    items: [
      evt({
        name: 'Questions',
        subtitle: 'Questions asked when guests sign up',
        url: '/events/:eventId/registration/questions',
        eventFeature: 'questions',
        component: EventQuestionsPage,
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Question',
            url: '/events/:eventId/registration/questions/add',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Question',
            url: '/events/:eventId/registration/questions/:id/edit',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Vouchers',
        subtitle: 'Issued vouchers and redemptions',
        url: '/events/:eventId/registration/coupons',
        eventFeature: 'vouchers',
        component: EventCouponsPage,
        featureCode: FEATURE_CODES.PROMOTIONS,
        gateKey: 'eventCoupons',
        icon: 'Gift',
        roles: ACCESS.EVENT_ADMIN,
        items: [
          evt({
            name: 'Generate Vouchers',
            url: '/events/:eventId/registration/coupons/add',
            component: EventCouponsPage,
            visible: false,
            icon: 'Gift',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Voucher Settings',
            url: '/events/:eventId/registration/coupons/rules',
            component: EventCouponsPage,
            visible: false,
            icon: 'Gift',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — a URL stage; it opens on a "ready" step, so a reload never runs the rules again.
          evt({
            name: 'Generate Vouchers from Rules',
            url: '/events/:eventId/registration/coupons/generate-from-rules',
            component: EventCouponsPage,
            visible: false,
            icon: 'Sparkles',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The chooser: pick which coupons to print and the stock to print them on.
          evt({
            name: 'Print Vouchers',
            url: '/events/:eventId/registration/coupons/print',
            component: EventCouponsPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ The SHEET itself, opened in a new tab by the chooser above. The design, the stock and
          // either the coupon filter or the selected ids travel as query params — see
          // EventCouponsPrintPage.
          evt({
            name: 'Voucher Sheet',
            url: '/events/:eventId/registration/coupons/print/sheet',
            component: EventCouponsPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Redeem Vouchers',
        subtitle: 'Scan and redeem vouchers at the vendor',
        url: '/events/:eventId/registration/coupons/redeem',
        eventFeature: 'vouchers',
        component: EventCouponRedeemPage,
        featureCode: FEATURE_CODES.PROMOTIONS,
        gateKey: 'couponRedeem',
        icon: 'Scan',
        roles: ACCESS.EVENT_VOUCHER_REDEEM,
      }),
      evt({
        name: 'Tickets & Pricing',
        subtitle: 'Ticket types and price rules',
        url: '/events/:eventId/registration',
        eventFeature: 'tickets',
        component: EventTicketsPage,
        featureCode: FEATURE_CODES.TICKETING,
        gateKey: 'ticketTypes',
        icon: 'Ticket',
        roles: ACCESS.EVENT_ADMIN,
        visibleWhen: ticketedOnly,
        items: [
          // Add / edit / price-rules / coupon-grants stages for a ticket — same page,
          // drawer driven by the URL.
          evt({
            name: 'Add Ticket',
            url: '/events/:eventId/registration/add',
            component: EventTicketsPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Ticket',
            url: '/events/:eventId/registration/:id/edit',
            component: EventTicketsPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Ticket Price Rules',
            url: '/events/:eventId/registration/:id/rules',
            component: EventTicketsPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Ticket Voucher Grants',
            url: '/events/:eventId/registration/:id/grants',
            component: EventTicketsPage,
            visible: false,
            icon: 'RosetteDiscount',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Discount Codes',
        subtitle: 'Checkout discount codes',
        url: '/events/:eventId/registration/discount-codes',
        eventFeature: 'tickets',
        component: EventDiscountCodesPage,
        // Every plan since 2026-10-22 (owner — docs/COMPETITIVE-BASELINE.md in eventsmagic-api): no plan lock.
        icon: 'Discount',
        roles: ACCESS.EVENT_ADMIN,
        visibleWhen: ticketedOnly,
        items: [
          evt({
            name: 'Add Discount Code',
            url: '/events/:eventId/registration/discount-codes/add',
            component: EventDiscountCodesPage,
            visible: false,
            icon: 'Discount',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Discount Code',
            url: '/events/:eventId/registration/discount-codes/:id/edit',
            component: EventDiscountCodesPage,
            visible: false,
            icon: 'Discount',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Box Office',
        subtitle: 'Sell tickets at the door and manage issued tickets',
        url: '/events/:eventId/ticket-sales',
        eventFeature: 'tickets',
        component: EventTicketSalesPage,
        featureCode: FEATURE_CODES.TICKETING,
        gateKey: 'ticketSales',
        icon: 'Ticket',
        roles: ACCESS.EVENT_ADMIN,
        visibleWhen: ticketedOnly,
        items: [
          evt({
            name: 'Sell at the Door',
            url: '/events/:eventId/ticket-sales/add',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #115 — the BIB drawers are URL stages, so a reload or a phone resumed from the background reopens them.
          evt({
            name: 'Generate BIB Numbers',
            url: '/events/:eventId/ticket-sales/bibs',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Assign BIB Number',
            url: '/events/:eventId/ticket-sales/:id/bib',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Hash',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — Generate tickets and the row's other steps are URL stages too (they opened from useState).
          evt({
            name: 'Generate Tickets',
            url: '/events/:eventId/ticket-sales/generate',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Change Ticket Type',
            url: '/events/:eventId/ticket-sales/:id/change-type',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Ticket',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Reassign Ticket',
            url: '/events/:eventId/ticket-sales/:id/reassign',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'User',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Refund Ticket',
            url: '/events/:eventId/ticket-sales/:id/refund',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The chooser: pick a ticket design and the stock to print it on.
          evt({
            name: 'Print Tickets',
            url: '/events/:eventId/ticket-sales/print',
            component: EventTicketSalesPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ The SHEET itself, opened in a new tab by the chooser above. The design, the stock and
          // either the ticket filter or the selected ids travel as query params — see
          // EventTicketsPrintPage.
          evt({
            name: 'Ticket Sheet',
            url: '/events/:eventId/ticket-sales/print/sheet',
            component: EventTicketsPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Transactions',
        subtitle: 'Payments and refunds for this event',
        url: '/events/:eventId/transactions',
        eventFeature: 'tickets',
        component: EventTransactionsPage,
        icon: 'CreditCard',
        roles: ACCESS.EVENT_ADMIN,
        visibleWhen: ticketedOnly,
        items: [
          evt({
            name: 'Transaction',
            url: '/events/:eventId/transactions/:paymentId',
            component: EventTransactionsPage,
            visible: false,
            icon: 'CreditCard',
            roles: ACCESS.EVENT_ADMIN,
          }),
          evt({
            name: 'Print Receipt',
            url: '/events/:eventId/transactions/:paymentId/receipt-print',
            component: ReceiptPrintPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.EVENT_ADMIN,
          }),
        ],
      }),
    ],
  }),

  // POLICIES & COMPLIANCE — the event's own feature (ROADMAP A2b #62 / #63, owner 2026-09-29: "Policies / compliance =
  // its own feature, used by RSVP and tickets"). It left Registration because it is switched on and off on its own: a
  // birthday party has no waivers, a race always does.
  // ⚠⚠ A TOP-LEVEL PAGE, NOT A ONE-CHILD SECTION — and the one deliberate exception to "every event page belongs to a
  // section", declared by name in `check-nav`. A section holding a single page is two taps for one destination (the
  // owner's "no single-child categories", 2026-07-26); a page named for the section IS the section until a second page
  // joins it. Its drawer stages stay children, hidden.
  // ⓘ Library → Legal Policies is the account's catalog of documents; this is their attachment to ONE event.
  evt({
    name: 'Policies & compliance',
    subtitle: 'Terms, waivers and consents guests accept',
    url: '/events/:eventId/compliance',
    eventFeature: 'policies',
    component: EventCompliancePage,
    featureCode: FEATURE_CODES.PRO_REGISTRATION,
    gateKey: 'compliance',
    icon: 'Gavel',
    roles: ACCESS.EVENT_ADMIN,
    items: [
      evt({
        name: 'Attach Policy',
        url: '/events/:eventId/compliance/add',
        component: EventCompliancePage,
        visible: false,
        icon: 'Gavel',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Edit Policy',
        url: '/events/:eventId/compliance/:id/edit',
        component: EventCompliancePage,
        visible: false,
        icon: 'Gavel',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Print Policy',
        url: '/events/:eventId/compliance/:id/print',
        component: PolicyPrintPage,
        visible: false,
        icon: 'Printer',
        roles: ACCESS.EVENT_MANAGE,
      }),
    ],
  }),

  // COMMUNICATIONS — invites, updates, reminders, messages, comments, inbox, sent.
  //
  // ⚠⚠ NO `url` AND NO `component`, DELIBERATELY — the SAME shape as Overview and Audience, and it
  // is load-bearing for the MOBILE TAB. `BottomTabBar.resolveTabUrl` prefers `item.url` and only
  // falls through to the first visible child when there is none. This group used to carry
  // `/events/:eventId/communications` + `component: PlaceholderPage` for a messaging hub that was
  // never built, so tab 4 — the most-tapped surface in the event workspace — landed on "Coming
  // soon" while all seven of its children were shipped and working.
  // ⚠ It was INVISIBLE from a desktop review: `DesktopSidebar` returns early for a group and
  // renders it as a collapsible list, and `MoreView` does the same, so only the phone tab bar ever
  // read that url. Give this group a `url` again and the bug comes straight back. Guarded by
  // `npm run check-nav`.
  evt({
    name: 'Communications',
    subtitle: 'Messages and announcements to your guests',
    component: undefined,
    bottomNavBar: 4,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'Mail',
    items: [
      evt({
        name: 'Invitations',
        subtitle: 'Invite guests with a personal RSVP link',
        url: '/events/:eventId/invitations',
        component: EventInvitationsPage,
        icon: 'Mail',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Event updates',
        subtitle: 'Send an update to your guests',
        url: '/events/:eventId/updates',
        component: EventUpdatesPage,
        icon: 'Speakerphone',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Reminders',
        subtitle: 'Schedule event-approaching & RSVP-nudge reminders',
        url: '/events/:eventId/reminders',
        component: RemindersPage,
        icon: 'Bell',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Add-reminder drawer — its own URL so a reload / deep-link / backgrounded-resume reopens it.
          // STATIC segment, so it ranks above the `:id/edit` param route below.
          evt({
            name: 'New reminder',
            url: '/events/:eventId/reminders/add',
            component: RemindersPage,
            visible: false,
            icon: 'Bell',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // Edit-reminder drawer (`:id/edit`) — its own route so a reload reopens the same reminder.
          evt({
            name: 'Reminder',
            url: '/events/:eventId/reminders/:id/edit',
            component: RemindersPage,
            visible: false,
            icon: 'Bell',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Custom messages',
        subtitle: 'Customize the copy guests see on forms, the website and emails',
        url: '/events/:eventId/messaging',
        component: EventMessagesPage,
        icon: 'FileText',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Slot editor drawer — its own URL stage so a reload / deep-link / backgrounded-resume reopens
          // the same slot. `:slotKey` is a catalog key (e.g. msg-rsvp-confirmation), never a record id.
          evt({
            name: 'Customize message',
            url: '/events/:eventId/messaging/:slotKey',
            component: EventMessagesPage,
            visible: false,
            icon: 'FileText',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Comments',
        subtitle: 'Guest-book & item comments from your website',
        url: '/events/:eventId/comments',
        component: EventCommentsPage,
        icon: 'Message',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Incoming messages',
        subtitle: 'Messages guests sent from your website',
        url: '/events/:eventId/inbox',
        component: IncomingMessagesPage,
        icon: 'Message',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Email-inbox settings drawer — its own URL so a reload/deep-link reopens it. STATIC segment,
          // so it ranks above the `:id` param route below (no collision with a counterpart value).
          evt({
            name: 'Email inbox settings',
            url: '/events/:eventId/inbox/settings',
            component: IncomingMessagesPage,
            visible: false,
            icon: 'Mail',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // URL-driven conversation drawer (`:id` = the counterpart email, URL-encoded) — its own route
          // so a reload / backgrounded-resume reopens the same conversation.
          evt({
            name: 'Conversation',
            url: '/events/:eventId/inbox/:id',
            component: IncomingMessagesPage,
            visible: false,
            icon: 'Message',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Sent messages',
        subtitle: 'What went out and who it reached',
        url: '/events/:eventId/messages',
        component: SentMessagesPage,
        icon: 'Send',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // URL-driven detail drawer — its own route so a reload (or a mobile browser resumed from
          // background) reopens the message the host was viewing. Same component, hidden from nav.
          evt({
            name: 'Message',
            url: '/events/:eventId/messages/:id',
            component: SentMessagesPage,
            visible: false,
            icon: 'Send',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // PROGRAM — the attendee-facing content and on-site experience.
  evt({
    name: 'Program',
    subtitle: 'Agenda, map and on-site experience',
    url: '',
    roles: ACCESS.EVENT_MANAGE,
    icon: 'CalendarEvent',
    items: [
      evt({
        name: 'Timeline',
        subtitle: 'Run-of-show: what happens before, during and after the event',
        url: '/events/:eventId/timeline',
        eventFeature: 'timeline',
        component: EventTimelinePage,
        icon: 'Timeline',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Timeline Item',
            url: '/events/:eventId/timeline/add',
            component: EventTimelinePage,
            visible: false,
            icon: 'Timeline',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ The RUN SHEET — the run-of-show on paper, opened in a new tab by the timeline page's ⋯
          // menu carrying that page's current filter. See EventRunSheetPrintPage.
          evt({
            name: 'Run Sheet',
            url: '/events/:eventId/timeline/print',
            component: EventRunSheetPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Timeline Item',
            url: '/events/:eventId/timeline/:id/edit',
            component: EventTimelinePage,
            visible: false,
            icon: 'Timeline',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Event Map',
        subtitle: 'Place stages, booths and facilities on a map',
        url: '/events/:eventId/map',
        eventFeature: 'event_map',
        component: EventMapPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventMap',
        icon: 'Map',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Map Element',
            url: '/events/:eventId/map/add',
            component: EventMapPage,
            visible: false,
            icon: 'Map',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Map Element',
            url: '/events/:eventId/map/:id/edit',
            component: EventMapPage,
            visible: false,
            icon: 'Map',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add Zone',
            url: '/events/:eventId/map/zones/add',
            component: EventMapPage,
            visible: false,
            icon: 'Vector',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Zone',
            url: '/events/:eventId/map/zones/:id/edit',
            component: EventMapPage,
            visible: false,
            icon: 'Vector',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Points of Interest',
        subtitle: 'Places, vendors and things for this event',
        url: '/events/:eventId/pois',
        eventFeature: 'pois',
        component: EventPoisPage,
        icon: 'MapPin',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Point of Interest',
            url: '/events/:eventId/pois/add',
            component: EventPoisPage,
            visible: false,
            icon: 'MapPin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Point of Interest',
            url: '/events/:eventId/pois/:id/edit',
            component: EventPoisPage,
            visible: false,
            icon: 'MapPin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — "Reorder categories" is a URL stage (it was a modal opened from useState).
          evt({
            name: 'Reorder Categories',
            url: '/events/:eventId/pois/category-order',
            component: EventPoisPage,
            visible: false,
            icon: 'MapPin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print POI Guide',
            url: '/events/:eventId/pois/print',
            component: PoiPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Polls & quizzes',
        subtitle: 'Questions guests answer on your event website',
        url: '/events/:eventId/poll-questions',
        eventFeature: 'polls',
        component: EventPollQuestionsPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'polls',
        icon: 'MessageCircle',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Question',
            url: '/events/:eventId/poll-questions/add',
            component: EventPollQuestionsPage,
            visible: false,
            icon: 'MessageCircle',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Question',
            url: '/events/:eventId/poll-questions/:id/edit',
            component: EventPollQuestionsPage,
            visible: false,
            icon: 'MessageCircle',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — "Reorder categories" is a URL stage (it was a modal opened from useState).
          evt({
            name: 'Reorder Categories',
            url: '/events/:eventId/poll-questions/category-order',
            component: EventPollQuestionsPage,
            visible: false,
            icon: 'MessageCircle',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Poll Form',
            url: '/events/:eventId/poll-questions/print',
            component: PollFormPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Signup list',
        subtitle: 'Things guests can sign up for',
        url: '/events/:eventId/signup-items',
        eventFeature: 'signup_list',
        component: EventSignupItemsPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'signupLists',
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Signup Item',
            url: '/events/:eventId/signup-items/add',
            component: EventSignupItemsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Signup Item',
            url: '/events/:eventId/signup-items/:id/edit',
            component: EventSignupItemsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — "Reorder categories" is a URL stage (it was a modal opened from useState).
          evt({
            name: 'Reorder Categories',
            url: '/events/:eventId/signup-items/category-order',
            component: EventSignupItemsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Signup Form',
            url: '/events/:eventId/signup-items/print',
            component: SignupFormPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Tallies',
        subtitle: 'Tap counters for headcounts, drinks, laps and more',
        url: '/events/:eventId/tallies',
        eventFeature: 'tallies',
        component: EventTalliesPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'tallies',
        icon: 'Number',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Tally',
            url: '/events/:eventId/tallies/add',
            component: EventTalliesPage,
            visible: false,
            icon: 'Number',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Tally',
            url: '/events/:eventId/tallies/:id/edit',
            component: EventTalliesPage,
            visible: false,
            icon: 'Number',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Race tracking',
        subtitle: 'Start waves and record finish times for sport events',
        url: '/events/:eventId/race',
        eventFeature: 'race',
        component: EventRaceTrackingPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'raceTracking',
        icon: 'Stopwatch',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Race results',
        subtitle: 'Leaderboard by bib and wave',
        url: '/events/:eventId/race/results',
        eventFeature: 'race',
        component: EventRaceResultsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'raceResults',
        icon: 'Trophy',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // ROADMAP #120 — "Correct times" is a URL stage (it was a modal opened from useState). A child, so it takes the
          // page's plan gate.
          evt({
            name: 'Correct Race Times',
            url: '/events/:eventId/race/results/:id/edit',
            component: EventRaceResultsPage,
            visible: false,
            icon: 'Trophy',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ⚠ ARTISTS ARE PROGRAMME, not a thing of their own. A booking's SET TIME is an
      // `event_time_slot` row pointing back at it (`event_artist_id`), the public site renders a
      // lineup block from it, and the host thinks about it beside the timeline. It sat as a
      // top-level leaf only because it arrived after the groups were drawn.
    evt({
      name: 'Artists',
      subtitle: 'Booked acts — lineup, deals and paperwork',
      url: '/events/:eventId/artists',
      eventFeature: 'artists',
      component: EventArtistsPage,
      featureCode: FEATURE_CODES.COMMERCIAL,
      gateKey: 'eventArtists',
      icon: 'Microphone',
      roles: ACCESS.EVENT_MANAGE,
      items: [
        evt({
          name: 'Book Act',
          url: '/events/:eventId/artists/add',
          component: EventArtistsPage,
                  visible: false,
          icon: 'Microphone',
          roles: ACCESS.EVENT_MANAGE,
        }),
        evt({
          name: 'Edit Booking',
          url: '/events/:eventId/artists/:id/edit',
          component: EventArtistsPage,
          visible: false,
          icon: 'Microphone',
          roles: ACCESS.EVENT_MANAGE,
        }),
        // #165 round 2 — copy the date shown (`?date=`) to other dates.
        evt({
          name: 'Copy Lineup',
          url: '/events/:eventId/artists/copy',
          component: EventArtistsPage,
          visible: false,
          icon: 'Microphone',
          roles: ACCESS.EVENT_MANAGE,
        }),
      ],
    }),
    ],
  }),

  // GAMES — prize draws and party games (ROADMAP A2b #63, owner 2026-09-29: games get their own section).
  //
  // ⚠⚠ THEIR URLS MOVED OUT OF `/guests/…` WITH THEM, and that is load-bearing for the phone section strip. The strip
  // lets a list keep claiming the drawer stages below it (`/guests/:id/edit` lights Guest List) ONLY while no OTHER
  // section's page sits under its url — `sectionNav.ownsDescendants`. Left at `/guests/draws`, the draws page would
  // have made Guest List match its own url exactly and nothing else, and every guest drawer would have lost its lit tab.
  // ⓘ No `url` and no `component`: a group with a url would make a phone tab ignore its children (see Communications).
  evt({
    name: 'Games',
    subtitle: 'Prize draws and party games',
    component: undefined,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'DeviceGamepad2',
    items: [
      evt({
        name: 'Prize Draws',
        subtitle: 'Random winners & raffles',
        url: '/events/:eventId/draws',
        eventFeature: 'prize_draws',
        component: EventDrawsPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'draws',
        icon: 'Dice',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Add / edit a round + run the draw — same page, URL-driven.
          evt({
            name: 'Add Prize Draw',
            url: '/events/:eventId/draws/add',
            component: EventDrawsPage,
            visible: false,
            icon: 'Dice',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Prize Draw',
            url: '/events/:eventId/draws/:id/edit',
            component: EventDrawsPage,
            visible: false,
            icon: 'Dice',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Run Prize Draw',
            url: '/events/:eventId/draws/:id/run',
            component: EventDrawsPage,
            visible: false,
            icon: 'Dice',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Winners',
            url: '/events/:eventId/draws/:id/winners-print',
            component: DrawWinnersPrintPage,
            visible: false,
            icon: 'FileText',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Party Games',
        subtitle: 'Secret roles & live rounds',
        url: '/events/:eventId/games',
        eventFeature: 'party_games',
        component: EventGamesPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'games',
        icon: 'Sparkles',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // Add / edit a game + play (deal rounds) — same page, URL-driven.
          evt({
            name: 'Add Game',
            url: '/events/:eventId/games/add',
            component: EventGamesPage,
            visible: false,
            icon: 'Sparkles',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Game',
            url: '/events/:eventId/games/:id/edit',
            component: EventGamesPage,
            visible: false,
            icon: 'Sparkles',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Play Game',
            url: '/events/:eventId/games/:id/play',
            component: EventGamesPage,
            visible: false,
            icon: 'PlayerPlay',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // OPERATIONS — the internal to-do side of running the event.
  evt({
    name: 'Operations',
    subtitle: 'Tasks, budget, crew logistics and files',
    url: '',
    roles: ACCESS.EVENT_MANAGE,
    icon: 'Checklist',
    items: [
      evt({
        name: 'Tasks & Appointments',
        subtitle: 'To-dos and appointments for this event',
        url: '/events/:eventId/tasks',
        eventFeature: 'tasks',
        component: EventTasksPage,
        icon: 'Checklist',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Task',
            url: '/events/:eventId/tasks/add',
            component: EventTasksPage,
            visible: false,
            icon: 'Checklist',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Task',
            url: '/events/:eventId/tasks/:id/edit',
            component: EventTasksPage,
            visible: false,
            icon: 'Checklist',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Budget',
        subtitle: 'Expected expenses and payments for this event',
        url: '/events/:eventId/budget',
        eventFeature: 'budget',
        component: EventBudgetPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'budget',
        icon: 'Coin',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Budget Item',
            url: '/events/:eventId/budget/add',
            component: EventBudgetPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Budget Item',
            url: '/events/:eventId/budget/:id/edit',
            component: EventBudgetPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Finance',
        subtitle: 'P&L and ROI — ticket revenue vs budget and partner costs',
        url: '/events/:eventId/finance',
        eventFeature: 'finance',
        component: EventFinancePage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'finance',
        icon: 'ReportMoney',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'P&L Report',
            url: '/events/:eventId/finance/print',
            component: EventFinancePrintPage,
            visible: false,
            icon: 'ReportMoney',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Forecast Report',
            url: '/events/:eventId/finance/forecast-print',
            component: EventForecastPrintPage,
            visible: false,
            icon: 'ChartBar',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Rentals',
        subtitle: 'Rented assets for this event — pickup/drop-off, cost and condition',
        url: '/events/:eventId/rentals',
        eventFeature: 'rentals',
        component: EventRentalsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventRentals',
        icon: 'Package',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Rental',
            url: '/events/:eventId/rentals/add',
            component: EventRentalsPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Rental',
            url: '/events/:eventId/rentals/:id/edit',
            component: EventRentalsPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Rental Media',
            url: '/events/:eventId/rentals/:id/media',
            component: EventRentalsPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ROADMAP #172 — codes for wristbands, cards and items, linked to a guest at the door (the check-in console).
      evt({
        name: 'QR tags',
        subtitle: 'Codes for wristbands, cards and items — linked to a guest at check-in, scanned any time',
        url: '/events/:eventId/qr-tags',
        eventFeature: 'qr_tags',
        component: EventQrTagsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventQrTags',
        icon: 'Qrcode',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add QR tag',
            url: '/events/:eventId/qr-tags/add',
            component: EventQrTagsPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Generate QR tags',
            url: '/events/:eventId/qr-tags/generate',
            component: EventQrTagsPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit QR tag',
            url: '/events/:eventId/qr-tags/:id/edit',
            component: EventQrTagsPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // #172 follow-up c — give a tag nobody holds to a guest (the edit drawer, opened on "Held by").
          evt({
            name: 'Give QR tag',
            url: '/events/:eventId/qr-tags/:id/give',
            component: EventQrTagsPage,
            visible: false,
            icon: 'UserPlus',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The chooser: which tags, on what stock, with what artwork.
          evt({
            name: 'Print QR tags',
            url: '/events/:eventId/qr-tags/print',
            component: EventQrTagsPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The sheet itself — opened in a new tab by the chooser (a bare page: the card engine's own shell).
          evt({
            name: 'QR tag sheet',
            url: '/events/:eventId/qr-tags/print/sheet',
            component: EventQrTagsPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ROADMAP #68 — what was found and what was lost: the crew log it, guests report it on the site, the desk hands it back.
      evt({
        name: 'Lost & found',
        subtitle: 'What was found and what was lost — guests report it, the desk hands it back',
        url: '/events/:eventId/lost-found',
        eventFeature: 'lost_found',
        component: EventLostFoundPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventLostFound',
        icon: 'Backpack',
        roles: ACCESS.EVENT_LOST_FOUND,
        items: [
          evt({
            name: 'Log found item',
            url: '/events/:eventId/lost-found/add',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Backpack',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Edit found item',
            url: '/events/:eventId/lost-found/:id/edit',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Backpack',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Receive found item',
            url: '/events/:eventId/lost-found/:id/receive',
            component: EventLostFoundPage,
            visible: false,
            icon: 'PackageImport',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Hand back found item',
            url: '/events/:eventId/lost-found/:id/return',
            component: EventLostFoundPage,
            visible: false,
            icon: 'HandStop',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Dispose of found item',
            url: '/events/:eventId/lost-found/:id/dispose',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Recycle',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Find item by code',
            url: '/events/:eventId/lost-found/scan',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Lost reports',
            url: '/events/:eventId/lost-found/reports',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Search',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Take lost report',
            url: '/events/:eventId/lost-found/reports/add',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Search',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Edit lost report',
            url: '/events/:eventId/lost-found/reports/:id/edit',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Search',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Match lost report',
            url: '/events/:eventId/lost-found/reports/:id/match',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Link',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          evt({
            name: 'Close lost report',
            url: '/events/:eventId/lost-found/reports/:id/close',
            component: EventLostFoundPage,
            visible: false,
            icon: 'CircleX',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
          // The settings are the event's (event.settings) — the page leaves the stage for anyone without them.
          evt({
            name: 'Lost & found settings',
            url: '/events/:eventId/lost-found/settings',
            component: EventLostFoundPage,
            visible: false,
            icon: 'Settings',
            roles: ACCESS.EVENT_LOST_FOUND,
          }),
        ],
      }),
      // ROADMAP #170 — the vendors working the event and the documents each owes (insurance, permits…), collected by a link.
      // A vendor's own page (`…/vendors/:id`) carries its drawers as URL stages.
      evt({
        name: 'Vendors',
        subtitle: 'Who works your event, and the insurance and permits each one owes',
        url: '/events/:eventId/vendors',
        eventFeature: 'vendors',
        component: EventVendorsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventVendors',
        icon: 'Certificate',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Ask vendors for documents',
            url: '/events/:eventId/vendors/ask',
            component: EventVendorsPage,
            visible: false,
            icon: 'FileCertificate',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Decision history',
            url: '/events/:eventId/vendors/history',
            component: EventVendorHistoryPage,
            visible: false,
            icon: 'History',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Venue review links',
            url: '/events/:eventId/vendors/venue-review',
            component: EventVendorsPage,
            visible: false,
            icon: 'FileCertificate',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add vendor',
            url: '/events/:eventId/vendors/add',
            component: EventVendorsPage,
            visible: false,
            icon: 'Plus',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Vendor',
            url: '/events/:eventId/vendors/:id',
            component: EventVendorPage,
            visible: false,
            icon: 'Certificate',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit vendor',
            url: '/events/:eventId/vendors/:id/edit',
            component: EventVendorPage,
            visible: false,
            icon: 'Pencil',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add vendor person',
            url: '/events/:eventId/vendors/:id/people/add',
            component: EventVendorPage,
            visible: false,
            icon: 'UserPlus',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Vendor person',
            url: '/events/:eventId/vendors/:id/people/:personId/edit',
            component: EventVendorPage,
            visible: false,
            icon: 'Pencil',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add vendor document',
            url: '/events/:eventId/vendors/:id/upload',
            component: EventVendorPage,
            visible: false,
            icon: 'Upload',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Ask for a document',
            url: '/events/:eventId/vendors/:id/documents/add',
            component: EventVendorPage,
            visible: false,
            icon: 'Plus',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit document request',
            url: '/events/:eventId/vendors/:id/documents/:requirementId/edit',
            component: EventVendorPage,
            visible: false,
            icon: 'Pencil',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Review vendor document',
            url: '/events/:eventId/vendors/:id/documents/:requirementId/review',
            component: EventVendorPage,
            visible: false,
            icon: 'FileCertificate',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Upload vendor document',
            url: '/events/:eventId/vendors/:id/documents/:requirementId/upload',
            component: EventVendorPage,
            visible: false,
            icon: 'Upload',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Travel',
        subtitle: 'Flights, trains and shuttles — who arrives when, and who is collecting them',
        url: '/events/:eventId/travel',
        eventFeature: 'travel',
        component: EventTravelPage,
        icon: 'Plane',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Travel',
            url: '/events/:eventId/travel/add',
            component: EventTravelPage,
            visible: false,
            icon: 'Plane',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Travel',
            url: '/events/:eventId/travel/:id/edit',
            component: EventTravelPage,
            visible: false,
            icon: 'Plane',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Accommodations',
        subtitle: 'Hotels and places to stay for this event',
        url: '/events/:eventId/accommodations',
        eventFeature: 'accommodations',
        component: EventAccommodationsPage,
        icon: 'BuildingSkyscraper',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Accommodation',
            url: '/events/:eventId/accommodations/add',
            component: EventAccommodationsPage,
            visible: false,
            icon: 'BuildingSkyscraper',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Accommodation',
            url: '/events/:eventId/accommodations/:id/edit',
            component: EventAccommodationsPage,
            visible: false,
            icon: 'BuildingSkyscraper',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #120 — "Reorder categories" is a URL stage (it was a modal opened from useState).
          evt({
            name: 'Reorder Categories',
            url: '/events/:eventId/accommodations/category-order',
            component: EventAccommodationsPage,
            visible: false,
            icon: 'BuildingSkyscraper',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Accommodation Form',
            url: '/events/:eventId/accommodations/print',
            component: AccommodationFormPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Files',
        subtitle: 'Documents, photos and media this event owns',
        url: '/events/:eventId/files',
        eventFeature: 'files',
        component: EventFilesPage,
        icon: 'Folder',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // A2e #99 — the unsent guest pictures: same page, the other view (a URL, so a reload keeps it).
          evt({
            name: 'Unsent pictures',
            url: '/events/:eventId/files/pending',
            component: EventFilesPage,
            visible: false,
            icon: 'Photo',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #130 — Upload and a file's Edit drawer are URL stages (they were dialogs on useState).
          evt({
            name: 'Upload Files',
            url: '/events/:eventId/files/upload',
            component: EventFilesPage,
            visible: false,
            icon: 'Upload',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit File',
            url: '/events/:eventId/files/:fileId/edit',
            component: EventFilesPage,
            visible: false,
            icon: 'Edit',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Notes',
        subtitle: 'Reminders, decisions and details for this event',
        url: '/events/:eventId/notes',
        eventFeature: 'notes',
        component: EventNotesPage,
        icon: 'Notes',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Note',
            url: '/events/:eventId/notes/add',
            component: EventNotesPage,
            visible: false,
            icon: 'Notes',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Note',
            url: '/events/:eventId/notes/:id/edit',
            component: EventNotesPage,
            visible: false,
            icon: 'Notes',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // INVENTORY — a TOP-LEVEL event group (not nested under Operations) so it stays
  // a 2-level menu. The mobile "More" view only renders top-level groups + their
  // direct children, so a group-inside-a-group (Operations › Inventory › pages)
  // is an unreachable dead tap on mobile; at the top level its pages are tappable
  // AND searchable there.
  evt({
    name: 'Inventory',
    subtitle: 'Event stock — receive, transfer, count and consume supplies',
    url: '',
    icon: 'Packages',
    roles: ACCESS.EVENT_MANAGE,
    items: [
      evt({
        name: 'Stock',
        key: 'eventInventoryStock',
        subtitle: 'On-hand stock at this event',
        url: '/events/:eventId/inventory/stock',
        eventFeature: 'inventory',
        component: EventInventoryStockPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventInventoryStock',
        icon: 'Package',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // ROADMAP #120 — "Write off stock" is a URL stage (it was a modal opened from useState). A child, so it takes
          // the page's plan gate.
          evt({
            name: 'Write Off Stock',
            url: '/events/:eventId/inventory/stock/:id/write-off',
            component: EventInventoryStockPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Operations',
        key: 'eventInventoryOperations',
        subtitle: 'Receive, transfer, count and consume',
        url: '/events/:eventId/inventory/operations',
        eventFeature: 'inventory',
        component: EventInventoryOperationsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventInventoryOps',
        icon: 'Scan',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'History',
        key: 'eventInventoryHistory',
        subtitle: 'Stock movement ledger',
        url: '/events/:eventId/inventory/history',
        eventFeature: 'inventory',
        component: EventInventoryHistoryPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'eventInventoryHistory',
        icon: 'History',
        roles: ACCESS.EVENT_MANAGE,
      }),
      evt({
        name: 'Inventory',
        url: '/events/:eventId/inventory',
        component: EventInventoryRedirect,
        visible: false,
        icon: 'Packages',
        roles: ACCESS.EVENT_MANAGE,
      }),
    ],
  }),

  // VOLUNTEERS — the shifts to staff, the people who sign up for them on the website, and the day-of check-in
  // (#A4.6: its own menu item, owner 2026-09-18).
  // ⓘ "Volunteers & staff" since #63 (owner, 2026-09-29) — staff pay lives here, and paid staff are not volunteers.
  // ⚠ Renaming a section resets its saved open/closed state in the sidebar (keyed by name) — once, harmlessly.
  evt({
    name: 'Volunteers & staff',
    subtitle: 'Shifts, sign-ups, check-in and staff pay',
    url: '',
    roles: ACCESS.EVENT_MANAGE,
    icon: 'HeartHandshake',
    items: [
      evt({
        name: 'Shifts',
        subtitle: 'Roles you need staffed — quantity, time, location and public sign-ups',
        url: '/events/:eventId/volunteers',
        eventFeature: 'volunteers',
        component: EventVolunteersPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteers',
        icon: 'Users',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Volunteer Slot',
            url: '/events/:eventId/volunteers/add',
            component: EventVolunteersPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Volunteer Slot',
            url: '/events/:eventId/volunteers/:id/edit',
            component: EventVolunteersPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Assign Crew',
            url: '/events/:eventId/volunteers/:slotId/crew',
            component: EventVolunteersPage,
            visible: false,
            icon: 'Users',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Volunteer Form',
            url: '/events/:eventId/volunteers/print',
            component: VolunteerSignupPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The crew's roster: every shift with its volunteers, to print and bring to the event.
          evt({
            name: 'Print Volunteer Roster',
            url: '/events/:eventId/volunteers/roster/print',
            component: VolunteerRosterPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // #A4.6 — sign-ups from the event website, decided per shift. ⚠ The detail drawer is a URL stage of its own,
      // or a reload and a shared link both 404.
      evt({
        name: 'Applications',
        subtitle: 'People who signed up for shifts on your event website',
        url: '/events/:eventId/volunteers/applications',
        eventFeature: 'volunteers',
        component: EventVolunteerApplicationsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteers',
        icon: 'Inbox',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Volunteer Application',
            url: '/events/:eventId/volunteers/applications/:id',
            component: EventVolunteerApplicationsPage,
            visible: false,
            icon: 'Inbox',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Check-in',
        subtitle: 'Mark volunteers arrived or gone — scan their QR — and track hours',
        url: '/events/:eventId/volunteers/checkin',
        eventFeature: 'volunteers',
        component: EventVolunteerCheckinPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteers',
        icon: 'UserCheck',
        // ⚠ EVENT_CHECKIN, not EVENT_MANAGE (owner ruling, 2026-09-22) — the same set the GUEST door
        // console uses. Door STAFF hold `checkin` and not `event.operate`, so they could already check
        // a volunteer in through the guest QR rail while this page was invisible to them: the same
        // person, the same door, two different answers. The API now accepts either capability for
        // reading the roster and for attendance; roster EDITS and PAY keep their stricter gates, and
        // the row's `rights` hide "Edit times" from anyone who may not use it.
        roles: ACCESS.EVENT_CHECKIN,
        items: [
          evt({
            name: 'Edit Attendance Times',
            url: '/events/:eventId/volunteers/checkin/:assignmentId',
            component: EventVolunteerCheckinPage,
            visible: false,
            icon: 'Clock',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // #A4.7 — paid volunteers: what the event's staff pay comes to, and recording payments. ⚠ Every drawer is a URL
      // stage of its own (settings, one person's Mark paid), or a reload and a shared link both 404.
      evt({
        name: 'Staff pay',
        subtitle: 'What paid volunteers earn — by role and per person — and what is still owed',
        url: '/events/:eventId/volunteers/staff-pay',
        eventFeature: 'volunteers',
        component: EventStaffPayPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteers',
        icon: 'Cash',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Staff Pay Rules',
            url: '/events/:eventId/volunteers/staff-pay/settings',
            component: EventStaffPayPage,
            visible: false,
            icon: 'Settings',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Mark Staff Paid',
            url: '/events/:eventId/volunteers/staff-pay/:eventGuestId/pay',
            component: EventStaffPayPage,
            visible: false,
            icon: 'Cash',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Print Staff Timesheet',
            url: '/events/:eventId/volunteers/staff-pay/print',
            component: StaffPayTimesheetPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ⚠ SAME COMPONENT as the RSVP and sponsorship questions pages — the page reads WHICH form from the path, so
      // these routes must exist or every link 404s while the page itself compiles perfectly.
      evt({
        name: 'Sign-up Questions',
        subtitle: 'What volunteers are asked when they sign up',
        url: '/events/:eventId/volunteers/questions',
        eventFeature: 'volunteers',
        component: EventQuestionsPage,
        featureCode: FEATURE_CODES.PRODUCTION,
        gateKey: 'volunteers',
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Volunteer Question',
            url: '/events/:eventId/volunteers/questions/add',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Volunteer Question',
            url: '/events/:eventId/volunteers/questions/:id/edit',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // PARTNERS — sponsors/partners, their packages and referral tracking.
  evt({
    name: 'Partners',
    subtitle: 'Sponsors, partners, packages, referrals and donations',
    url: '',
    roles: ACCESS.EVENT_MANAGE,
    icon: 'Award',
    items: [
      evt({
        name: 'Sponsors & Partners',
        subtitle: 'Confirmed partners — and the money they bring in',
        url: '/events/:eventId/partners',
        eventFeature: 'partners',
        component: EventPartnersPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'eventPartners',
        icon: 'Award',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Partner',
            url: '/events/:eventId/partners/add',
            component: EventPartnersPage,
            visible: false,
            icon: 'Award',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Partner',
            url: '/events/:eventId/partners/:id/edit',
            component: EventPartnersPage,
            visible: false,
            icon: 'Award',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Partner Payments',
            url: '/events/:eventId/partners/:partnerId/payments',
            component: EventPartnersPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ EVERY DRAWER OVER THE PAYMENTS DRAWER IS A URL STAGE (2026-09-18) — record, edit and
          // refund used to be `useState` toggles, so a reload or a phone resumed from the background
          // dropped the host out of a half-entered refund. Same page component; it reads the stage
          // from the path. An unregistered stage 404s while the page compiles perfectly.
          evt({
            name: 'Record Partner Payment',
            url: '/events/:eventId/partners/:partnerId/payments/add',
            component: EventPartnersPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Partner Payment',
            url: '/events/:eventId/partners/:partnerId/payments/:paymentId/edit',
            component: EventPartnersPage,
            visible: false,
            icon: 'Coin',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Refund Partner Payment',
            url: '/events/:eventId/partners/:partnerId/payments/:paymentId/refund',
            component: EventPartnersPage,
            visible: false,
            icon: 'ArrowBackUp',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ Opened in a NEW TAB, so nothing navigates to it in-app — registered for the same reason
          // as the sponsorship receipt below.
          evt({
            name: 'Print Partner Payment Receipt',
            url: '/events/:eventId/partners/:partnerId/payments/:paymentId/receipt-print',
            component: PartnerPaymentReceiptPrintPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Packages Offered',
        subtitle: 'Packages open for this event',
        url: '/events/:eventId/partners/packages',
        eventFeature: 'partners',
        component: EventPartnerPackagesPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'eventPartnerPackages',
        icon: 'Package',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // ⚠ Both stages must be registered or the drawer's own URL 404s on reload, however well
          // the page handles it. `packages` is a STATIC segment so it outranks the sibling
          // `/partners/:id/edit` — the two never collide.
          evt({
            name: 'Offer a Package',
            url: '/events/:eventId/partners/packages/add',
            component: EventPartnerPackagesPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Offering',
            url: '/events/:eventId/partners/packages/:id/edit',
            component: EventPartnerPackagesPage,
            visible: false,
            icon: 'Package',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Applications',
        subtitle: 'Sponsors who have asked to join this event',
        url: '/events/:eventId/partners/applications',
        eventFeature: 'partners',
        component: EventPartnerApplicationsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'eventPartnerApplications',
        icon: 'Inbox',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          // ⚠ The detail drawer is a URL stage of its own, or a reload and a shared link both 404.
          evt({
            name: 'Application',
            url: '/events/:eventId/partners/applications/:id',
            component: EventPartnerApplicationsPage,
            visible: false,
            icon: 'Inbox',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ⚠ Opened in a NEW TAB from the drawer, so nothing navigates to it in-app — which is
          // exactly why it still has to be registered here: an unregistered route 404s while the
          // page that opens it compiles perfectly.
          evt({
            name: 'Print Sponsorship Receipt',
            url: '/events/:eventId/partners/applications/:id/receipt-print',
            component: PartnerReceiptPrintPage,
            visible: false,
            icon: 'ReceiptTax',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ⚠ SAME COMPONENT as the RSVP questions page, parameterized by the form (#A4.5, ruling 1 —
      // reuse the RSVP question editor rather than building a second one). The page reads which form
      // it is editing from the PATH, so these three routes must exist or every link 404s while the
      // page itself compiles perfectly.
      evt({
        name: 'Application Questions',
        subtitle: 'What sponsors are asked when they apply',
        url: '/events/:eventId/partners/questions',
        eventFeature: 'partners',
        component: EventQuestionsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'eventPartnerQuestions',
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Application Question',
            url: '/events/:eventId/partners/questions/add',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Application Question',
            url: '/events/:eventId/partners/questions/:id/edit',
            component: EventQuestionsPage,
            visible: false,
            icon: 'ClipboardList',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Referrals',
        subtitle: 'Partner link click tracking',
        url: '/events/:eventId/partners/referrals',
        // 🔴 NOT BUILT — hidden from the menu until it is. It falls through to
        // PlaceholderPage (`acc`/`evt` default), and a menu item that opens "Coming soon"
        // is a promise the product does not keep. ⚠ The ROUTE stays registered, so an old
        // deep link still resolves; only the nav entry goes. Partner link click tracking is not implemented anywhere.
        // ▶ Un-hide it in the SAME change that gives it a `component:`. `npm run check-nav`
        // fails if a VISIBLE entry lands on PlaceholderPage, so this cannot come back quietly.
        visible: false,
        icon: 'Click',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // #204 step 4 — the causes the event collects for, its gifts (online and at the door) and "Take a donation".
      evt({
        name: 'Donations',
        subtitle: 'Causes, gifts and donations at the door',
        url: '/events/:eventId/donations',
        eventFeature: 'donations',
        component: EventDonationsPage,
        featureCode: FEATURE_CODES.COMMERCIAL,
        gateKey: 'eventDonations',
        icon: 'Heart',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Causes for this Event',
            url: '/events/:eventId/donations/causes',
            component: EventDonationsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Take a Donation',
            url: '/events/:eventId/donations/record',
            component: EventDonationsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Gift',
            url: '/events/:eventId/donations/:id/view',
            component: EventDonationsPage,
            visible: false,
            icon: 'Heart',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  // ARTISTS — the booked acts on this event's lineup and their deals / paperwork.

  // MARKETING — promotion tracking. A single page today (becomes a category as
  // more marketing tools land).

  // WEBSITE — the public event site builder (a leaf; its edit stages are hidden).

  // THEME — the website's look (fonts, colors, buttons). Its own page so it can
  // grow element overrides + custom CSS. Selecting a theme writes event.themeId.

  // ANALYTICS — the reports that read back what guests told the host. The landing page is a real
  // index now (it was a PlaceholderPage while there was one report under it; there are four).
  // ⚠⚠ THE PUBLIC SITE IS A SECTION, NOT THREE STRAY LEAVES. "Website", "Theme" and "Marketing
  // Channels" each sat at the top level next to Guests and Operations, so nothing said they were
  // one job — and a leaf called "Website" reads like a link TO the site rather than a builder OF
  // it, which is exactly how it was misread. The group is now named for the thing, and the child
  // is named for the work: Website → Pages.
  // ⚠ Marketing Channels belongs here because the links it generates point AT this site — the page
  // exists to produce a per-channel URL and QR for the event page. Its REPORT (visits, unique
  // visits, conversions) does not exist yet and is ROADMAP A2b #59; nothing records a visit today.
  evt({
    name: 'Website',
    subtitle: 'The public event site — pages, look and links',
    component: undefined,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'World',
    items: [
      evt({
        name: 'Pages',
        subtitle: 'The pages and content of the public event site',
        url: '/events/:eventId/website',
        component: EventWebsitePage,
        icon: 'World',
        roles: ACCESS.EVENT_ADMIN,
        items: [
          evt({
            name: 'Add Page',
            url: '/events/:eventId/website/add',
            component: EventWebsitePage,
            visible: false,
            icon: 'World',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Page',
            url: '/events/:eventId/website/:id/edit',
            component: EventWebsitePage,
            visible: false,
            icon: 'World',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // The block editor for one page (its own page, not a drawer).
          evt({
            name: 'Edit Content',
            url: '/events/:eventId/website/:pageId/blocks',
            component: EventPageEditorPage,
            visible: false,
            icon: 'LayoutGrid',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Add Block',
            url: '/events/:eventId/website/:pageId/blocks/add',
            component: EventPageEditorPage,
            visible: false,
            icon: 'LayoutGrid',
            roles: ACCESS.EVENT_MANAGE,
          }),
          evt({
            name: 'Edit Block',
            url: '/events/:eventId/website/:pageId/blocks/:id/edit',
            component: EventPageEditorPage,
            visible: false,
            icon: 'LayoutGrid',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Theme',
        subtitle: 'Fonts, colors and buttons',
        url: '/events/:eventId/theme',
        component: EventThemePage,
        icon: 'Palette',
        roles: ACCESS.EVENT_ADMIN,
      }),
      // B20 — the full-screen preview of the theme as THIS event adjusts it (a URL stage, as the theme editor's).
      evt({
        name: 'Preview Event Theme',
        url: '/events/:eventId/theme/preview',
        component: EventThemePage,
        visible: false,
        icon: 'Palette',
        roles: ACCESS.EVENT_ADMIN,
      }),
      evt({
        name: 'Marketing Channels',
        subtitle: 'Tracking links & QR codes per channel for this event',
        url: '/events/:eventId/marketing-channels',
        eventFeature: 'marketing_channels',
        component: EventMarketingChannelsPage,
        // Every plan since 2026-10-22 (owner — docs/COMPETITIVE-BASELINE.md in eventsmagic-api): no plan lock.
        icon: 'Speakerphone',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add Marketing Channel',
            url: '/events/:eventId/marketing-channels/add',
            component: EventMarketingChannelsPage,
                    visible: false,
            icon: 'Speakerphone',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #130 — a channel's QR code is a URL stage (it was a modal on useState).
          evt({
            name: 'Channel QR Code',
            url: '/events/:eventId/marketing-channels/:channelId/qr',
            component: EventMarketingChannelsPage,
            visible: false,
            icon: 'Qrcode',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
    ],
  }),

  evt({
    name: 'Analytics',
    subtitle: 'Read back what your guests told you',
    url: '/events/:eventId/analytics',
    component: EventAnalyticsPage,
    roles: ACCESS.EVENT_MANAGE,
    icon: 'ChartBar',
    items: [
      evt({
        name: 'Question Answers',
        subtitle: 'What guests told you, counted — for planning food and the rest',
        url: '/events/:eventId/analytics/questions',
        eventFeature: 'questions',
        component: EventQuestionReportPage,
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Question Answers Report',
            url: '/events/:eventId/analytics/questions/print',
            component: EventQuestionReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #236 — "Print answers by guest", a stage of the report. The page leaves it for whoever
          // `canReadAnswerSheet` refuses (door staff hold guests.view and still may not: allergies are health data).
          evt({
            name: 'Print Answers by Guest',
            url: '/events/:eventId/analytics/questions/by-guest',
            component: EventQuestionReportPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
          // ROADMAP #236 — the guests' answers sheet, opened from that stage AND from the Guest list's Print drawer.
          // The server refuses it without Analytics & reports; the sheet shows the refusal.
          evt({
            name: 'Guests Answers Sheet',
            url: '/events/:eventId/analytics/questions/answers/print',
            component: EventAnswerSheetPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // SIGNUP LIST report (#44). Carries the SAME feature gate as the Signup list page it
      // reports on — an analytics entry for a feature the host's plan does not include would
      // advertise something they cannot reach.
      evt({
        name: 'Polls & Quizzes',
        subtitle: 'What guests voted for — and who got it right',
        url: '/events/:eventId/analytics/polls',
        eventFeature: 'polls',
        component: EventPollReportPage,
        icon: 'MessageCircle',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Polls Report',
            url: '/events/:eventId/analytics/polls/print',
            component: EventPollReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // TRAVEL report (#48). ⚠ No featureCode: the Travel page it reports on carries none.
      evt({
        name: 'Signup List',
        subtitle: 'Who is covering what — and what is still open',
        url: '/events/:eventId/analytics/signups',
        eventFeature: 'signup_list',
        component: EventSignupReportPage,
        featureCode: FEATURE_CODES.ENGAGEMENT,
        gateKey: 'signupLists',
        icon: 'ClipboardList',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Signup List Report',
            url: '/events/:eventId/analytics/signups/print',
            component: EventSignupReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // POINTS OF INTEREST report (#46). ⚠ No featureCode: the Points of Interest page it reports
      // on carries none either, and a gate here that the feature itself does not have would lock a
      // host out of reading data they were allowed to collect.
      evt({
        name: 'Points of Interest',
        subtitle: 'Who is going where — and what guests wish they could',
        url: '/events/:eventId/analytics/pois',
        eventFeature: 'pois',
        component: EventPoiReportPage,
        icon: 'MapPin',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Points of Interest Report',
            url: '/events/:eventId/analytics/pois/print',
            component: EventPoiReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // POLLS & QUIZZES report (#45). ⚠ No featureCode: the Polls page it reports on carries none.
      evt({
        name: 'Accommodations',
        subtitle: 'Who is staying where, and how many beds per night',
        url: '/events/:eventId/analytics/stays',
        eventFeature: 'accommodations',
        component: EventAccommodationReportPage,
        icon: 'BuildingSkyscraper',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Accommodations Report',
            url: '/events/:eventId/analytics/stays/print',
            component: EventAccommodationReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      evt({
        name: 'Travel',
        subtitle: 'Who lands when — and who is collecting them',
        url: '/events/:eventId/analytics/travel',
        eventFeature: 'travel',
        component: EventTravelReportPage,
        icon: 'Plane',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Travel Report',
            url: '/events/:eventId/analytics/travel/print',
            component: EventTravelReportPrintPage,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // TICKET SALES (#183) — what was sold, before who came. A ticketed event's only (or one with selling data, as the
      // Box Office). ⚠ On `insights`, bound here; the server refuses the read regardless. `EventAnalyticsPage`'s grid
      // lists it in the same place.
      evt({
        name: 'Ticket Sales',
        subtitle: 'What was sold, for how much, and through which channel',
        url: '/events/:eventId/analytics/tickets',
        component: EventTicketSalesReportPage,
        featureCode: FEATURE_CODES.INSIGHTS,
        gateKey: 'ticketSalesReport',
        icon: 'Ticket',
        roles: ACCESS.EVENT_MANAGE,
        visibleWhen: ticketedOnly,
        items: [
          evt({
            name: 'Ticket Sales Report',
            url: '/events/:eventId/analytics/tickets/print',
            component: EventTicketSalesReportPrintPage,
            // #238 — a print: not opened by a draft or a test run without the plan.
            output: true,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ATTENDANCE (#179) — who actually came, after how they travel and before how they heard of it. ⚠ On `insights`
      // (owner, 2026-10-17: the reports are the upsell): bound here, so a host without it sees the upgrade screen — and
      // the server refuses the read regardless. Its print sheet inherits the binding.
      evt({
        name: 'Attendance',
        subtitle: 'Who came, when, through where — and how long they stayed',
        url: '/events/:eventId/analytics/arrivals',
        component: EventArrivalReportPage,
        featureCode: FEATURE_CODES.INSIGHTS,
        gateKey: 'attendanceReport',
        icon: 'Login',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Attendance Report',
            url: '/events/:eventId/analytics/arrivals/print',
            component: EventArrivalReportPrintPage,
            // #238 — a print: not opened by a draft or a test run without the plan.
            output: true,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ADD-ONS, VOUCHERS, VOLUNTEERS (#180) — the operations behind the attendance, each on `insights` like it (bound here; the
      // server refuses the read regardless). `EventAnalyticsPage`'s grid lists them in the same order.
      evt({
        name: 'Add-ons',
        subtitle: 'What was sold, handed out — and what is still to hand out',
        url: '/events/:eventId/analytics/addons',
        component: EventAddonReportPage,
        featureCode: FEATURE_CODES.INSIGHTS,
        gateKey: 'addonReport',
        icon: 'ShoppingBag',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Add-ons Report',
            url: '/events/:eventId/analytics/addons/print',
            component: EventAddonReportPrintPage,
            // #238 — a print: not opened by a draft or a test run without the plan.
            output: true,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // #180b — redemptions by voucher, item or stall.
      evt({
        name: 'Vouchers',
        subtitle: 'What was redeemed, where, and what it is worth',
        url: '/events/:eventId/analytics/vouchers',
        component: EventVoucherReportPage,
        featureCode: FEATURE_CODES.INSIGHTS,
        gateKey: 'voucherReport',
        icon: 'Gift',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Vouchers Report',
            url: '/events/:eventId/analytics/vouchers/print',
            component: EventVoucherReportPrintPage,
            // #238 — a print: not opened by a draft or a test run without the plan.
            output: true,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // #180c — shifts, check-ins and hours (staff pay measure).
      evt({
        name: 'Volunteers',
        subtitle: 'Who came for their shifts, where they reported, and the hours worked',
        url: '/events/:eventId/analytics/volunteers',
        component: EventVolunteerReportPage,
        featureCode: FEATURE_CODES.INSIGHTS,
        gateKey: 'volunteerReport',
        icon: 'HeartHandshake',
        roles: ACCESS.EVENT_MANAGE,
        items: [
          evt({
            name: 'Volunteers Report',
            url: '/events/:eventId/analytics/volunteers/print',
            component: EventVolunteerReportPrintPage,
            // #238 — a print: not opened by a draft or a test run without the plan.
            output: true,
            visible: false,
            icon: 'Printer',
            roles: ACCESS.EVENT_MANAGE,
          }),
        ],
      }),
      // ⚠ MARKETING is last in this group on purpose, and `EventAnalyticsPage`'s card grid repeats
      // the same order — the nav and the index disagreeing makes the page feel unrelated to the
      // sidebar beside it.
      evt({
        name: 'Traffic and conversions',
        subtitle: 'Which marketing channels brought people to this event',
        url: '/events/:eventId/analytics/marketing',
        eventFeature: 'marketing_channels',
        component: EventMarketingReportPage,
        // Every plan since 2026-10-22 (owner — docs/COMPETITIVE-BASELINE.md in eventsmagic-api): no plan lock.
        icon: 'Speakerphone',
        roles: ACCESS.EVENT_MANAGE,
      }),
      // ACCOMMODATIONS report (#47). Same reasoning on the gate as the POI entry above.
    ],
  }),

  // COMPLIANCE — waivers, consents and their acceptances (a leaf; stages hidden).
];

// ============================================
// PUBLIC PAGES
// ============================================
const publicSiteMap: SiteMapItem[] = [
  {
    name: 'Sign Up',
    subtitle: 'Create your account',
    url: '/account/signup',
    component: SignupPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'signUp' },
  },
  {
    // Non-account crew sign-in (docs/CREW-ACCESS.md). Opened via ?inv=<token>.
    name: 'Crew Access',
    subtitle: 'Open your event as crew',
    url: '/crew-access',
    component: CrewAccessPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
  },
  {
    name: 'Sign In',
    subtitle: 'Sign in to your account',
    url: '/account/signin',
    component: SigninPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'signIn' },
  },
  {
    name: 'Forgot Password',
    subtitle: 'Request a password reset link',
    url: '/account/forgot-password',
    component: ForgotPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'forgotPassword' },
  },
  {
    name: 'Reset Password',
    subtitle: 'Reset your password',
    url: '/account/reset-password',
    component: ResetPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'resetPassword' },
  },
  {
    name: 'Accept Invitation',
    subtitle: 'Join an account',
    url: '/account/accept-invitation',
    component: AcceptInvitationPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'acceptInvitation' },
  },
  {
    name: 'Onboarding',
    subtitle: 'Set up your account',
    url: '/onboarding',
    component: OnboardingPage,
    visible: false,
    roles: ALL_ROLES,
    items: [],
    options: { translationKey: 'onboarding' },
    icon: 'Rocket',
  },
  {
    name: 'Server Error Page',
    subtitle: '',
    url: '/server-error',
    component: ServerErrorPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'serverError' },
  },
  {
    name: 'Page Not Found',
    subtitle: '',
    url: '*',
    component: NotFoundPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: { translationKey: 'notFound' },
  },
];

// ============================================
// Which pages bring their own shell (ROADMAP A2b #58)
// ============================================

/**
 * ⭐ THE ONE LIST OF PAGES THAT MUST **NOT** GET THE APP SHELL.
 *
 * Since #58 the sidebar + header + bottom tab bar are rendered ABOVE the route by `AppShellLayout`
 * (passed to the router in `App.tsx`), so every page gets them by default and none renders the
 * layout itself. These bring their own full-screen shell instead and would nest one inside the
 * other:
 *   · every `*PrintPage` — a sheet that goes to a printer, inside `PrintPage`;
 *   · the create-event wizard and onboarding — deliberately distraction-light flows;
 *   · the public auth screens (`AuthLayout`) and the 404 / 500 screens (`FullScreenLayout`).
 *
 * ⚠ LISTED BY COMPONENT REFERENCE, NOT BY NAME OR URL. A string list would go stale silently the
 * first time a page was renamed, and there is no honest way to tell a typo from a deletion.
 * ⚠⚠ APPLIED ONCE, OVER THE ASSEMBLED TREE, RATHER THAN AS A FLAG ON ~39 SCATTERED ENTRIES — and
 * it has to unwrap the plan gate to do it, because `gateNode` replaces `component` with
 * `withPlanGate(component)` and INHERITS down to children, so a print sub-route inside a gated
 * module is not the component this list names. Matching the wrapper's `__wrapped` is what keeps
 * "this page prints" independent of "this module is on a paid plan".
 * ▶ `npm run check-app-shell` fails on a page that is in neither camp — one that publishes chrome
 * into a shell that is not there, or sits in the shell with no chrome to put in its header.
 */
const BARE_SHELL_COMPONENTS: React.ComponentType<any>[] = [
  // Public — their own AuthLayout / FullScreenLayout.
  SigninPage,
  SignupPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  AcceptInvitationPage,
  CrewAccessPage,
  NotFoundPage,
  ServerErrorPage,
  // Focused flows with their own shell.
  OnboardingPage,
  CreateEventPage,
  // Print sheets — every one of these renders `PrintPage`.
  AccommodationFormPrintPage,
  DrawWinnersPrintPage,
  EventAccommodationReportPrintPage,
  EventBadgesPrintPage,
  EventCallSheetPrintPage,
  EventCouponsPrintPage,
  EventQrTagsPrintPage,
  EventFinancePrintPage,
  EventForecastPrintPage,
  EventPoiReportPrintPage,
  EventPollReportPrintPage,
  EventQuestionReportPrintPage,
  EventAnswerSheetPrintPage,
  EventRunSheetPrintPage,
  EventSignupReportPrintPage,
  EventTicketsPrintPage,
  EventTravelReportPrintPage,
  EventArrivalReportPrintPage,
  EventTicketSalesReportPrintPage,
  EventAddonReportPrintPage,
  EventVoucherReportPrintPage,
  EventVolunteerReportPrintPage,
  GuestListPrintPage,
  InventoryLabelsPrintPage,
  PartnerPaymentReceiptPrintPage,
  PartnerReceiptPrintPage,
  PoiPrintPage,
  PolicyPrintPage,
  PollFormPrintPage,
  PurchaseOrderPrintPage,
  ReceiptPrintPage,
  SignupFormPrintPage,
  SpendingReceiptPrintPage,
  StaffPayTimesheetPrintPage,
  VolunteerRosterPrintPage,
  VolunteerSignupPrintPage,
];

/** Is this route's page one that renders its own full-screen shell? */
const isBareShell = (component?: React.ComponentType<any>): boolean => {
  if (!component) {
    return false;
  }

  // ⚠ Unwrap the plan gate first — see the note on BARE_SHELL_COMPONENTS.
  const target = ((component as any).__wrapped as React.ComponentType<any>) ?? component;

  return BARE_SHELL_COMPONENTS.includes(target);
};

/** Stamp `layout: BareLayout` on every route whose page brings its own shell. */
const applyShellLayout = (items: SiteMapItem[]): SiteMapItem[] =>
  items.map((item) => ({
    ...item,
    ...(isBareShell(item.component) ? { layout: BareLayout } : null),
    items: item.items?.length ? applyShellLayout(item.items) : item.items,
  }));

const siteMap: SiteMapItem[] = applyShellLayout([...accountSiteMap, ...eventSiteMap, ...publicSiteMap]);

// ============================================
// Route path registry (URLs live in ONE place — here)
// ============================================

/** Walk the sitemap collecting every item that declares a `key` → its `url`. */
const collectRoutePaths = (items: SiteMapItem[], acc: Record<string, string> = {}): Record<string, string> => {
  for (const item of items) {
    if (item.key && item.url) acc[item.key] = item.url;
    if (item.items?.length) collectRoutePaths(item.items, acc);
  }
  return acc;
};

/** key → base URL, derived from the sitemap. */
export const ROUTE_PATHS: Record<string, string> = collectRoutePaths(siteMap);

/**
 * Resolve a route's base path by its stable sitemap key. Components use this
 * instead of hardcoding URL fragments, so URL changes only touch the sitemap.
 * Falls back to "/" if the key is unknown.
 */
export const getRoutePath = (key: string): string => ROUTE_PATHS[key] ?? '/';

/**
 * Start downloading a nav entry's page chunk, without navigating.
 *
 * Call it on the first hint of intent — `onPointerEnter` on a sidebar row, `onPointerDown` on a tab.
 * ⚠ Deliberately silent and best-effort: a failed prefetch must never surface, because the real
 * navigation will import the same module again and THAT failure is the one worth showing. A rejected
 * promise here with no catch would also trip the unhandled-rejection reporter for nothing.
 */
export const preloadRoute = (item?: SiteMapItem | null): void => {
  const component = item?.component as (React.ComponentType<any> & { preload?: () => Promise<unknown> }) | undefined;

  try {
    void component?.preload?.().catch(() => undefined);
  } catch {
    // Never let a prefetch break a render or a pointer handler.
  }
};

export default siteMap;
